import React, { useState, useEffect, useContext, useRef  } from 'react';
import { useNavigate } from 'react-router-dom'; // Add this import
import { v4 as uuidv4 } from 'uuid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faTrash,
  faTimes,
  faCheck,
  faChevronDown,
  faSearch,
  faX,
  faArrowRight,
  faCalendarAlt,
  // Use these alternatives if the above icons don't exist
  faCog as faRules,           // or faListAlt, faClipboardList
  faCoins,                    // this should exist
  faCheckCircle,              // this should exist  
  faClock, 
  faSpinner,                    // this should exist
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { 
  ArrowRight, 
  CheckCircle,
} from 'lucide-react';
import { Spinner } from '../Loader/Loader';
import { decryptData } from '../../utils/storageCrypto';
const RewardRuleContext = React.createContext();

// Context Provider Component
const RewardRuleProvider = ({ children }) => {
  const [sharedData, setSharedData] = useState({
    selectedCategory: null,
    selectedBrand: null,
    fetchedItems: []
  });

  return (
    <RewardRuleContext.Provider value={{ sharedData, setSharedData }}>
      {children}
    </RewardRuleContext.Provider>
  );
};

// Custom hook to use the context
const useRewardRuleContext = () => {
  const context = useContext(RewardRuleContext);
  if (!context) {
    throw new Error('useRewardRuleContext must be used within RewardRuleProvider');
  }
  return context;
};

// Minimalistic Card Components
const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200 ${className}`}>
    {children}
  </div>
);

const CardContent = ({ children, className = "" }) => (
  <div className={`p-6 ${className}`}>
    {children}
  </div>
);

// Modern Button Component
const Button = ({ 
  children, 
  variant = "primary", 
  size = "md", 
  className = "", 
  onClick,
  disabled = false,
  ...props 
}) => {
  const baseClasses = "inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";
  
  const variants = {
    primary: "bg-gradient-to-r from-[#f7525a] to-[#e6474f] text-white hover:from-[#e6474f] hover:to-[#d63c43] focus:ring-[#f7525a]/20 shadow-sm hover:shadow-md",
    secondary: "bg-gray-50 text-gray-700 hover:bg-gray-100 focus:ring-gray-500/20 border border-gray-200",
    outline: "border border-gray-200 text-gray-700 hover:bg-gray-50 focus:ring-gray-500/20",
    destructive: "bg-red-50 text-red-600 hover:bg-red-100 focus:ring-red-500/20 border border-red-200",
    ghost: "text-gray-600 hover:text-gray-900 hover:bg-gray-50 focus:ring-gray-500/20"
  };
  
  const sizes = {
    sm: "px-2 py-1 text-sm",
    md: "px-2 py-1 text-sm",
    lg: "px-2 py-1 text-sm"
  };
  
  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

// Modern Input Component
const Input = ({ 
  className = "", 
  type = "text", 
  placeholder = "",
  value,
  onChange,
  disabled = false,
  icon,
  ...props 
}) => {
  return (
    <div className="relative">
      {icon && (
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
          <FontAwesomeIcon icon={icon} className="text-sm" />
        </div>
      )}
      <input
        type={type}
        className={`w-full px-2 py-1 text-sm bg-gray-50 border-0 rounded-md focus:bg-white focus:ring-2 focus:ring-[#f7525a]/20 focus:outline-none transition-all duration-200 placeholder-gray-400 ${icon ? 'pl-10' : ''} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        disabled={disabled}
        {...props}
      />
    </div>
  );
};

// Update the Select component to pass options to SelectValue
const Select = ({ children, value, onValueChange, className = "", placeholder = "Select option", searchable = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value);
  const [searchTerm, setSearchTerm] = useState('');

  // Extract options from children for SelectValue
  const options = React.Children.toArray(children)
    .filter(child => child.type === SelectItem)
    .map(child => ({
      value: child.props.value,
      label: child.props.children,
      name: child.props.children
    }));

  // Update selectedValue when value prop changes
  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  const handleSelect = (newValue) => {
    setSelectedValue(newValue);
    if (onValueChange) {
      onValueChange(newValue);
    }
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearchTerm('');
  };

  // Filter children based on search term if searchable
  const filteredChildren = searchable && searchTerm ? 
    React.Children.toArray(children).filter(child => {
      if (child.type === SelectItem) {
        const childText = typeof child.props.children === 'string' 
          ? child.props.children.toLowerCase() 
          : '';
        return childText.includes(searchTerm.toLowerCase());
      }
      return true;
    }) : children;

  return (
    <div className={`relative ${className}`}>
      <SelectTrigger onClick={() => setIsOpen(!isOpen)} value={selectedValue}>
        <SelectValue placeholder={placeholder} value={selectedValue} options={options} />
      </SelectTrigger>
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-gray-100 rounded-xl shadow-lg max-h-60 overflow-hidden">
          {searchable && (
            <div className="p-1 border-b border-gray-100">
              <div className="relative">
                <FontAwesomeIcon 
                  icon={faSearch} 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
                />
                <input
                  type="text"
                  className="w-full pl-10 pr-10 p-1 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-[#f7525a]/20 focus:outline-none transition-all duration-200 placeholder-gray-400"
                  placeholder="Search options..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
                {searchTerm && (
                  <button
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSearchTerm('');
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} className="text-xs" />
                  </button>
                )}
              </div>
            </div>
          )}
          <SelectContent>
            {React.Children.count(filteredChildren) > 0 ? (
              React.Children.map(filteredChildren, (child, index) => {
                if (child.type === SelectItem) {
                  return React.cloneElement(child, { 
                    key: child.key || index,
                    onSelect: handleSelect, 
                    onClose: handleClose,
                    isSelected: child.props.value === selectedValue
                  });
                }
                return child;
              })
            ) : (
              searchable && searchTerm ? (
                <div className="px-4 py-8 text-center">
                  <div className="text-gray-400 text-sm">
                    No options found for "{searchTerm}"
                  </div>
                </div>
              ) : (
                <div className="px-4 py-8 text-center">
                  <div className="text-gray-400 text-sm">
                    No options available
                  </div>
                </div>
              )
            )}
          </SelectContent>
        </div>
      )}
    </div>
  );
};

const SelectTrigger = ({ children, onClick, value, className = "" }) => (
  <button
    type="button"
    className={`w-full px-2 py-1 text-sm bg-gray-50 rounded-md focus:bg-white focus:ring-2 focus:ring-[#f7525a]/20 focus:outline-none transition-all duration-200 flex items-center justify-between hover:bg-gray-100 ${className}`}
    onClick={onClick}
  >
    {children}
    <FontAwesomeIcon icon={faChevronDown} className="text-gray-400 text-xs" />
  </button>
);

// Update the SelectValue component to handle label display
const SelectValue = ({ placeholder, value, options = [], displayKey = null }) => {
  let displayValue = placeholder;
  
  if (value) {
    if (options && options.length > 0) {
      // Find the option that matches the value
      const selectedOption = options.find(option => {
        if (displayKey) {
          return option[displayKey] === value || option.value === value || option.id === value;
        }
        return option.value === value || option.label === value || option.name === value || option === value;
      });
      
      if (selectedOption) {
        // Display the label/name instead of value
        displayValue = selectedOption.label || selectedOption.name || selectedOption[displayKey] || selectedOption.value || selectedOption;
      } else {
        displayValue = value;
      }
    } else {
      displayValue = value;
    }
  }
  
  return (
    <span className={value ? "text-gray-900" : "text-gray-400"}>
      {displayValue}
    </span>
  );
};

const SelectContent = ({ children }) => (
  <div className="py-2 max-h-48 overflow-y-auto">
    {children}
  </div>
);

// COMPLETELY FIXED SelectItem component with proper error handling
const SelectItem = ({ children, value, onSelect, onClose, isSelected }) => {
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Safety check for onSelect function
    if (onSelect && typeof onSelect === 'function') {
      onSelect(value);
    } else {
      console.warn('SelectItem: onSelect is not a function or is undefined');
    }
    
    // Safety check for onClose function
    if (onClose && typeof onClose === 'function') {
      onClose();
    }
  };

  return (
    <button
      type="button"
      className={`w-full px-2 py-1 text-sm text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors duration-150 ${
        isSelected ? 'bg-gray-100 text-[#f7525a]' : ''
      }`}
      onClick={handleClick}
    >
      {children}
    </button>
  );
};


// Operators array
const operators = [
  { label: "is", value: "==" },
  { label: "is not", value: "!=" },
  { label: "is greater than", value: ">" },
  { label: "is greater than or equal to", value: ">=" },
  { label: "is less than", value: "<" },
  { label: "is less than or equal to", value: "<=" },
  { label: "contains", value: "contains" },
  { label: "does not contain", value: "not_contains" },
  { label: "starts with", value: "starts_with" },
  { label: "ends with", value: "ends_with" },
  { label: "is empty", value: "is_empty" },
  { label: "is not empty", value: "is_not_empty" },
  { label: "exists", value: "exists" },
  { label: "does not exist", value: "not_exists" },
  { label: "is in", value: "in" },
  { label: "is not in", value: "not_in" },
  { label: "includes any of", value: "includes_any" },
  { label: "includes all of", value: "includes_all" },
  { label: "is before", value: "before" },
  { label: "is after", value: "after" },
  { label: "is between", value: "between" },
  { label: "matches pattern", value: "regex" },
  { label: "is like", value: "like" }
];

const cardSteps = [
  {
    key: "reward",
    label: "Reward Rules",
    substeps: ["When/Where", "How Many"]
  },
  {
    key: "redemption", 
    label: "Redemption Rules",
    substeps: ["Applicability", "How Many Coins"]
  },
  {
    key: "expiry",
    label: "Expiry Rules", 
    substeps: ["Expiry Configuration"]
  }
];

// Modern Multi-Select Dropdown
const MultiSelectDropdown = ({ options, selectedValues, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredOptions = options.filter(option =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const handleOptionToggle = (optionId, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    let newSelectedValues;
    
    if (optionId === "all") {
      if (selectedValues.includes("all") || isAllIndividualItemsSelected()) {
        newSelectedValues = [];
      } else {
        const individualOptions = options.filter(opt => opt.id !== "all");
        newSelectedValues = individualOptions.map(option => option.id);
      }
    } else {
      if (selectedValues.includes(optionId)) {
        newSelectedValues = selectedValues.filter(id => id !== optionId);
      } else {
        newSelectedValues = [...selectedValues, optionId];
      }
      
      newSelectedValues = newSelectedValues.filter(id => id !== "all");
    }
    
    onChange(newSelectedValues);
  };

  const isAllIndividualItemsSelected = () => {
    const individualOptions = options.filter(opt => opt.id !== "all");
    const selectedIndividualOptions = selectedValues.filter(id => id !== "all");
    return selectedIndividualOptions.length === individualOptions.length && individualOptions.length > 0;
  };

  const getDisplayText = () => {
    if (selectedValues.length === 0) return placeholder || "Select items";
    
    if (isAllIndividualItemsSelected()) {
      return "All items selected";
    }
    
    if (selectedValues.length === 1) {
      const option = options.find(opt => opt.id === selectedValues[0]);
      return option ? option.name : "";
    }
    return `${selectedValues.length} items selected`;
  };

  return (
    <div className="relative flex-1">
      <button
        type="button"
        className="w-full px-2 py-1 text-sm bg-gray-50 rounded-md focus:bg-white focus:ring-2 focus:ring-[#f7525a]/20 focus:outline-none transition-all duration-200 flex items-center justify-between hover:bg-gray-100"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={selectedValues.length === 0 ? "text-gray-400" : "text-gray-900"}>
          {getDisplayText()}
        </span>
        <FontAwesomeIcon 
          icon={faChevronDown} 
          className={`text-gray-400 text-xs transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-gray-100 rounded-xl shadow-lg max-h-80 overflow-hidden">
          {/* Search Header */}
          <div className="p-3 border-b border-gray-100">
            <div className="relative">
              <FontAwesomeIcon 
                icon={faSearch} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
              />
              <input
                type="text"
                className="w-full pl-10 pr-10 py-2 text-sm bg-gray-50 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-[#f7525a]/20 focus:outline-none transition-all duration-200 placeholder-gray-400"
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
              {searchTerm && (
                <button
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSearchTerm('');
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              )}
            </div>
          </div>
          
          {/* Options List */}
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const isChecked = option.id === "all" 
                  ? isAllIndividualItemsSelected() 
                  : selectedValues.includes(option.id);

                return (
                  <label
                    key={option.id}
                    className="flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors group"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => handleOptionToggle(option.id, e)}
                        className="sr-only"
                      />
                      <div className={`w-4 h-4 rounded border-2 transition-all duration-200 ${
                        isChecked 
                          ? 'bg-[#f7525a] border-[#f7525a]' 
                          : 'border-gray-300 group-hover:border-gray-400'
                      }`}>
                        {isChecked && (
                          <FontAwesomeIcon 
                            icon={faCheck} 
                            className="text-white text-xs absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" 
                          />
                        )}
                      </div>
                    </div>
                    <span className="ml-3 text-sm text-gray-900 select-none">
                      {option.name}
                    </span>
                  </label>
                );
              })
            ) : (
              <div className="px-4 py-8 text-center">
                <div className="text-gray-400 text-sm">
                  {searchTerm ? `No items found for "${searchTerm}"` : 'No items available'}
                </div>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="p-3 border-t border-gray-100 bg-gray-50 rounded-b-xl">
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-500 font-medium">
                {selectedValues.length} selected
              </span>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange([]);
                  }}
                  className="text-xs px-3 py-1"
                >
                  Clear All
                </Button>
                <Button
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className="text-xs px-3 py-1"
                >
                  Done
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Update the DateInput component to handle proper date conversion
const DateInput = ({ value, onChange, placeholder = "DD/MM/YYYY" }) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [showCalendar, setShowCalendar] = useState(false);

  const validateAndFormatDate = (input) => {
    let cleaned = input.replace(/[^\d/]/g, '');
    
    if (cleaned.length >= 2 && cleaned.indexOf('/') === -1) {
      cleaned = cleaned.substring(0, 2) + '/' + cleaned.substring(2);
    }
    if (cleaned.length >= 5 && cleaned.lastIndexOf('/') === 2) {
      cleaned = cleaned.substring(0, 5) + '/' + cleaned.substring(5);
    }
    
    cleaned = cleaned.substring(0, 10);
    return cleaned;
  };

  // Convert DD/MM/YYYY to ISO date string for backend
  const convertToISODate = (displayDate) => {
    if (!displayDate || !displayDate.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      return null;
    }
    
    try {
      const [day, month, year] = displayDate.split('/');
      // Create date in ISO format (YYYY-MM-DD)
      const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      
      // Validate the date
      const dateObj = new Date(isoDate);
      if (dateObj.getFullYear() == year && 
          dateObj.getMonth() == month - 1 && 
          dateObj.getDate() == day) {
        return isoDate;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  // Convert ISO date to DD/MM/YYYY for display
  const convertFromISODate = (isoDate) => {
    if (!isoDate) return '';
    
    try {
      if (isoDate.match(/^\d{4}-\d{2}-\d{2}/)) {
        const [year, month, day] = isoDate.split('T')[0].split('-');
        return `${day}/${month}/${year}`;
      }
      return isoDate;
    } catch (error) {
      return isoDate;
    }
  };

  // Initialize display value from prop
  useEffect(() => {
    setInputValue(convertFromISODate(value) || '');
  }, [value]);

  const formatDateForInput = (displayDate) => {
    if (!displayDate) return '';
    
    try {
      if (displayDate.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
        const [day, month, year] = displayDate.split('/');
        if (day && month && year && day.length === 2 && month.length === 2 && year.length === 4) {
          return `${year}-${month}-${day}`;
        }
      }
      
      if (displayDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return displayDate;
      }
      
      return '';
    } catch (error) {
      return '';
    }
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  const handleTextInput = (e) => {
    const formatted = validateAndFormatDate(e.target.value);
    setInputValue(formatted);
    
    // Convert to ISO format before sending to parent
    const isoDate = convertToISODate(formatted);
    onChange(isoDate);
  };

  const handleCalendarInput = (e) => {
    const isoDate = e.target.value; // This is already in YYYY-MM-DD format
    const displayDate = formatDateForDisplay(isoDate);
    setInputValue(displayDate);
    onChange(isoDate); // Send ISO date to parent
    setShowCalendar(false);
  };

  //date calender not opening
/*   return (
    <div className="relative">
      <div className="flex gap-2">
        <Input
          value={inputValue}
          onChange={handleTextInput}
          placeholder={placeholder}
          maxLength="10"
          className="flex-1"
          icon={faCalendarAlt}
        />
        <div className="relative">
          <input
            type="date"
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            value={formatDateForInput(inputValue)}
            onChange={handleCalendarInput}
            style={{ zIndex: 10 }}
          />
          <Button
            variant="outline"
            size="lg"
            className="px-3 pointer-events-none"
          >
            <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-400" />
          </Button>
        </div>
      </div>
      {inputValue && inputValue.length === 10 && !convertToISODate(inputValue) && (
        <p className="text-xs text-red-500 mt-1 ml-1">
          Please enter a valid date (DD/MM/YYYY)
        </p>
      )}
    </div>
  ); */
  const dateInputRef = useRef(null);

const handleButtonClick = () => {
  if (dateInputRef.current) {
    dateInputRef.current.showPicker(); // Modern browsers
    // Fallback for older browsers
    dateInputRef.current.focus();
    dateInputRef.current.click();
  }
};

return (
  <div className="relative">
    <div className="flex gap-2">
      <Input
        value={inputValue}
        onChange={handleTextInput}
        placeholder={placeholder}
        maxLength="10"
        className="flex-1"
        icon={faCalendarAlt}
      />
      <div className="relative">
        <input
          ref={dateInputRef}
          type="date"
          className="absolute inset-0 w-full h-full opacity-0 pointer-events-none"
          value={formatDateForInput(inputValue)}
          onChange={handleCalendarInput}
        />
        <Button
          variant="outline"
          size="lg"
          className="p-4"
          onClick={handleButtonClick}
        >
          <FontAwesomeIcon 
            icon={faCalendarAlt} 
            className="text-gray-400"
          />
        </Button>
      </div>
    </div>
    {inputValue && inputValue.length === 10 && !convertToISODate(inputValue) && (
      <p className="text-xs text-red-500 mt-1 ml-1">
        Please enter a valid date (DD/MM/YYYY)
      </p>
    )}
  </div>
);

};


// Reward Criteria Substep Component
const RewardCriteriaSubstep = ({ onNext, onCancel,formData, updateFormData, mode }) => {
  const { sharedData, setSharedData } = useRewardRuleContext();
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([
    { id: "all", name: "All" },
    { id: "hyderabad", name: "Hyderabad" },
    { id: "vijayawada", name: "Vijayawada" },
    { id: "guntur", name: "Guntur" },
    { id: "visakhapatnam", name: "Visakhapatnam" }
  ]);

  const operators = [
  { label: "is", value: "==" },
  { label: "is not", value: "!=" },
  { label: "is greater than", value: ">" },
  { label: "is greater than or equal to", value: ">=" },
  { label: "is less than", value: "<" },
  { label: "is less than or equal to", value: "<=" },
  { label: "contains", value: "contains" },
  { label: "does not contain", value: "not_contains" },
  { label: "starts with", value: "starts_with" },
  { label: "ends with", value: "ends_with" },
  { label: "is empty", value: "is_empty" },
  { label: "is not empty", value: "is_not_empty" },
  { label: "exists", value: "exists" },
  { label: "does not exist", value: "not_exists" },
  { label: "is in", value: "in" },
  { label: "is not in", value: "not_in" },
  { label: "includes any of", value: "includes_any" },
  { label: "includes all of", value: "includes_all" },
  { label: "is before", value: "before" },
  { label: "is after", value: "after" },
  { label: "is between", value: "between" },
  { label: "matches pattern", value: "regex" },
  { label: "is like", value: "like" }
];

const customerTypes = [
  {id:"Individual Buyer",name:"Individual Buyer"},
  {id:"Home Builder",name:"Home Builder"},
  {id:"Pro Builder",name:"Pro Builder"},
  {id :"Contractor",name:"Contractor"}


]

  // Add conditional operators for combining conditions
  const conditionalOperators = [
    { label: "AND", value: "AND", description: "All conditions must be true" },
    { label: "OR", value: "OR", description: "At least one condition must be true" },
    { label: "NOT", value: "NOT", description: "Condition must not be true" }
  ];

  const fields = [
    { label: "Category", value: "category", type: "select", options: categories },
    { label: "Brand", value: "brand", type: "select", options: brands },
    { label: "Amount", value: "amount" },
    { label: "Is Ecommerce", value: "is_ecommerce", type: "select", options: [
      { id: "true", name: "Yes" },
      { id: "false", name: "No" }
    ]},
    { label: "Campaign", value: "campaign" },
    { label: "Event", value: "event" },
    { label: "Location", value: "location", type: "multiselect", options: locations },
    { label: "Items", value: "items", type: "multiselect", options: items },
    { label: "Buyer Type", value:"customer_type", type:"select", options: customerTypes}
  ];


  // Use formData instead of local state
  const rewardCriteria = formData.rewardCriteria;

/*   const [rewardCriteria, setRewardCriteria] = useState([
    {
      id: uuidv4(), 
      name: "",
      fromDate: "",
      toDate: "", 
      conditions: []
    }
  ]); */

   useEffect(() => {
      fetchCategories();
      fetchBrands();
      fetchLocations();
    }, []);

    // Fetch items when both category and brand are selected and share with context
  useEffect(() => {
    if (sharedData.selectedCategory && sharedData.selectedBrand) {
      fetchItemsForSharing(sharedData.selectedCategory.id, sharedData.selectedBrand.id);
    }
  }, [sharedData.selectedCategory, sharedData.selectedBrand]);

      // Fetch Categories
  const fetchCategories = async () => {
    const encryptedToken = sessionStorage.getItem('token');
        const token = decryptData(encryptedToken);
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}product-category/get-all-productCategories`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(data)
      const formattedCategories = (data.data).map(cat => ({
        id:cat._id,
        name:cat.product_category_name
      }));
      setCategories(formattedCategories); 
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Brands
  const fetchBrands = async () => {
    const token = sessionStorage.getItem('token');
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}vendor/get-all-vendors`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
           'x-token': token
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(data)
      const formattedBrands = (data.data).map(brand => ({
        id:brand._id,
        name:brand.name
      }));
      setBrands(formattedBrands); 
    } catch (error) {
      console.error('Error fetching brands:', error);
      setBrands([]);
    } finally {
      setLoading(false);
    }
  };

  //Fetch Items
/* const fetchItems = async (categoryId, brandId) => {
  if (!categoryId || !brandId) {
    setItems([]);
    return;
  }

  const token = sessionStorage.getItem('token');
  try {
    setLoading(true);
    const response = await fetch(
      `${process.env.REACT_APP_FORE_BRICKBUCKET}product/get-products-by-category-and-vendor?product_category_id=${categoryId}&vendor_id=${brandId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('Items data:', data);
    
    const formattedItems = (data.data || []).map(item => ({
      id: item._id,
      name: item.product_name || item.name
    }));
    
    setItems(formattedItems);
  } catch (error) {
    console.error('Error fetching items:', error);
    setItems([]);
  } finally {
    setLoading(false);
  }
}; */

// Fetch items and share with other components
  const fetchItemsForSharing = async (categoryId, brandId) => {
    if (!categoryId || !brandId) {
      setSharedData(prev => ({ ...prev, fetchedItems: [] }));
      return;
    }

    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_FORE_BRICKBUCKET}product/get-products-by-category-and-vendor?product_category_id=${categoryId}&vendor_id=${brandId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-token': token
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Items data for sharing:', data);
      
      const formattedItems = [
        { id: "all", name: "All Items" },
        ...(data.data || []).map(item => ({
          id: item._id,
          name: item.product_name || item.name
        }))
      ];
      
      // Share items with other components
      setSharedData(prev => ({ ...prev, fetchedItems: formattedItems }));
      setItems(formattedItems);
      
    } catch (error) {
      console.error('Error fetching items:', error);
      setSharedData(prev => ({ ...prev, fetchedItems: [{ id: "all", name: "All Items" }] }));
      setItems([{ id: "all", name: "All Items" }]);
    } finally {
      setLoading(false);
    }
  };


  //Fetch Locations
const fetchLocations = async () => {
  const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);
  try {
    setLoading(true);
    const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}hub/get-all-hubs`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-token': token
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('Locations data:', data);
    
    // Format locations with "All" option first, then actual locations
    const formattedLocations = [
      { id: "all", name: "All" }, // Keep the "All" option
      ...(data.data || []).map(location => ({
        id: location._id,
        name: location.city || location.name || location.hub_name // Use city field or fallback
      }))
    ];
    
    setLocations(formattedLocations);
  } catch (error) {
    console.error('Error fetching locations:', error);
    // Fallback to static locations if API fails
    setLocations([
      { id: "all", name: "All" },
      { id: "hyderabad", name: "Hyderabad" },
      { id: "vijayawada", name: "Vijayawada" },
      { id: "guntur", name: "Guntur" },
      { id: "visakhapatnam", name: "Visakhapatnam" }
    ]);
  } finally {
    setLoading(false);
  }
};

const generateRewardCriteriaName = (criteria) => {
  if (!criteria.conditions || criteria.conditions.length === 0) {
    if (criteria.fromDate && criteria.toDate) {
      return `Rule applies between ${criteria.fromDate} and ${criteria.toDate}`;
    }
    return "New Reward Criteria";
  }
  
  // Filter out incomplete conditions
  const validConditions = criteria.conditions.filter(cond => 
    cond.field && 
    cond.operator && 
    (cond.value !== "" && cond.value !== null && cond.value !== undefined)
  );
  
  if (validConditions.length === 0) {
    if (criteria.fromDate && criteria.toDate) {
      return `Rule applies between ${criteria.fromDate} and ${criteria.toDate}`;
    }
    return "New Reward Criteria";
  }
  
  const conditionStrings = validConditions.map(cond => {
    const fieldLabel = fields.find(f => f.value === cond.field)?.label || cond.field;
    const operatorLabel = operators.find(op => op.value === cond.operator)?.label || cond.operator;
    
    let valueString = "";
    
    if (cond.field === "location" && Array.isArray(cond.value)) {
      const locationNames = cond.value.map(locId => {
        const location = locations.find(loc => loc.id === locId);
        return location ? location.name : locId;
      });
      valueString = locationNames.length > 2 
        ? `${locationNames.slice(0, 2).join(", ")} and ${locationNames.length - 2} more` 
        : locationNames.join(", ");
    } else if (cond.field === "items" && Array.isArray(cond.value)) {
      const itemNames = cond.value.map(itemId => {
        const item = items.find(i => i.id === itemId);
        return item ? item.name : itemId;
      });
      valueString = itemNames.length > 2 
        ? `${itemNames.slice(0, 2).join(", ")} and ${itemNames.length - 2} more` 
        : itemNames.join(", ");
    } else if (cond.field === "category") {
      // Handle category field
      const category = categories.find(cat => cat.name === cond.value || cat.id === cond.value);
      valueString = category ? category.name : cond.value.toString();
    } else if (cond.field === "brand") {
      // Handle brand field
      const brand = brands.find(b => b.name === cond.value || b.id === cond.value);
      valueString = brand ? brand.name : cond.value.toString();
    } else if (cond.field === "amount") {
      valueString = `₹${cond.value}`;
    } else if (cond.field === "is_ecommerce") {
      valueString = cond.value === "true" || cond.value === true ? "Yes" : "No";
    } else if (Array.isArray(cond.value)) {
      valueString = cond.value.join(", ");
    } else {
      valueString = cond.value.toString();
    }
    
    return `${fieldLabel} ${operatorLabel} ${valueString}`;
  });
  
  if (conditionStrings.length === 0) {
    if (criteria.fromDate && criteria.toDate) {
      return `Rule applies between ${criteria.fromDate} and ${criteria.toDate}`;
    }
    return "New Reward Criteria";
  }
  
  let baseStatement;
  if (conditionStrings.length === 1) {
    baseStatement = `When ${conditionStrings[0]}`;
  } else if (conditionStrings.length === 2) {
    baseStatement = `When ${conditionStrings[0]} and ${conditionStrings[1]}`;
  } else {
    baseStatement = `When ${conditionStrings[0]}, ${conditionStrings[1]} and ${conditionStrings.length - 2} more conditions`;
  }
  
  // Add date range if both dates are present
  if (criteria.fromDate && criteria.toDate) {
    baseStatement += `, applies between ${criteria.fromDate} and ${criteria.toDate}`;
  } else if (criteria.fromDate) {
    baseStatement += `, applies from ${criteria.fromDate}`;
  } else if (criteria.toDate) {
    baseStatement += `, applies until ${criteria.toDate}`;
  }
  
  return baseStatement;
};


  // CRUD functions
/* const updateRewardCriteriaName = (criteriaId, name) => {
    setRewardCriteria(prev => 
      prev.map(criteria => 
        criteria.id === criteriaId ? { ...criteria, name } : criteria
      )
    );
  }; */

  const updateRewardCriteriaName = (criteriaId, name) => {
    const updatedCriteria = rewardCriteria.map(criteria => 
      criteria.id === criteriaId ? { ...criteria, name } : criteria
    );
    updateFormData('rewardCriteria', updatedCriteria);
  };

/* const updateRewardCriteriaFromDate = (criteriaId, fromDate) => {
    setRewardCriteria(prev => 
      prev.map(criteria => {
        if (criteria.id === criteriaId) {
          const updatedCriteria = { ...criteria, fromDate };
          // Auto-generate name if it's empty or default
          if (!updatedCriteria.name || updatedCriteria.name === "" || updatedCriteria.name === "New Reward Criteria") {
            updatedCriteria.name = generateRewardCriteriaName(updatedCriteria);
          }
          return updatedCriteria;
        }
        return criteria;
      })
    );
  }; */


  const updateRewardCriteriaFromDate = (criteriaId, fromDate) => {
    const updatedCriteria = rewardCriteria.map(criteria => {
      if (criteria.id === criteriaId) {
        const updatedCriteria = { ...criteria, fromDate };
        if (!updatedCriteria.name || updatedCriteria.name === "" || updatedCriteria.name === "New Reward Criteria") {
          updatedCriteria.name = generateRewardCriteriaName(updatedCriteria);
        }
        return updatedCriteria;
      }
      return criteria;
    });
    updateFormData('rewardCriteria', updatedCriteria);
  };


/*  const updateRewardCriteriaToDate = (criteriaId, toDate) => {
    setRewardCriteria(prev => 
      prev.map(criteria => {
        if (criteria.id === criteriaId) {
          const updatedCriteria = { ...criteria, toDate };
          // Auto-generate name if it's empty or default
          if (!updatedCriteria.name || updatedCriteria.name === "" || updatedCriteria.name === "New Reward Criteria") {
            updatedCriteria.name = generateRewardCriteriaName(updatedCriteria);
          }
          return updatedCriteria;
        }
        return criteria;
      })
    );
  }; */

  const updateRewardCriteriaToDate = (criteriaId, toDate) => {
    const updatedCriteria = rewardCriteria.map(criteria => {
      if (criteria.id === criteriaId) {
        const updatedCriteria = { ...criteria, toDate };
        if (!updatedCriteria.name || updatedCriteria.name === "" || updatedCriteria.name === "New Reward Criteria") {
          updatedCriteria.name = generateRewardCriteriaName(updatedCriteria);
        }
        return updatedCriteria;
      }
      return criteria;
    });
    updateFormData('rewardCriteria', updatedCriteria);
  };

  /* const addRewardCriteriaCondition = (criteriaId) => {
    setRewardCriteria(prev => 
      prev.map(criteria => 
        criteria.id === criteriaId 
          ? { 
              ...criteria, 
              conditions: [...criteria.conditions, { 
                id: uuidv4(), 
                field: "", 
                operator: "==", 
                value: "" 
              }] 
            }
          : criteria
      )
    );
  };
 */

  const addRewardCriteriaCondition = (criteriaId) => {
    const updatedCriteria = rewardCriteria.map(criteria => 
      criteria.id === criteriaId 
        ? { 
            ...criteria, 
            conditions: [...criteria.conditions, { 
              id: uuidv4(), 
              field: "", 
              operator: "==", 
              value: "" ,
            conditionalOperator: criteria.conditions.length > 0 ? "AND" : null // Add conditional operator for non-first conditions
            }] 
          }
        : criteria
    );
    updateFormData('rewardCriteria', updatedCriteria);
  };

   // Enhanced updateRewardCriteriaCondition to track and share category and brand selection
/*   const updateRewardCriteriaCondition = (criteriaId, condId, key, value) => {
    setRewardCriteria(prev => 
      prev.map(criteria => {
        if (criteria.id === criteriaId) {
          const updatedCriteria = {
            ...criteria,
            conditions: criteria.conditions.map(cond => {
              if (cond.id === condId) {
                const updatedCond = { ...cond, [key]: value };
                
                // Track and share category and brand selection
                if (key === 'value') {
                  if (cond.field === 'category') {
                    const selectedCategory = categories.find(cat => cat.name === value);
                    if (selectedCategory) {
                      setSharedData(prev => ({ 
                        ...prev, 
                        selectedCategory: selectedCategory 
                      }));
                    }
                  } else if (cond.field === 'brand') {
                    const selectedBrand = brands.find(brand => brand.name === value);
                    if (selectedBrand) {
                      setSharedData(prev => ({ 
                        ...prev, 
                        selectedBrand: selectedBrand 
                      }));
                    }
                  }
                }
                
                return updatedCond;
              }
              return cond;
            })
          };
          
          // Auto-generate name if it's empty or default
          if (!updatedCriteria.name || updatedCriteria.name === "" || updatedCriteria.name === "New Reward Criteria") {
            updatedCriteria.name = generateRewardCriteriaName(updatedCriteria);
          }
          return updatedCriteria;
        }
        return criteria;
      })
    );
  }; */

const updateRewardCriteriaCondition = (criteriaId, condId, key, value) => {
  const updatedCriteria = rewardCriteria.map(criteria => {
    if (criteria.id === criteriaId) {
      const updatedCriteria = {
        ...criteria,
        conditions: criteria.conditions.map(cond => {
          if (cond.id === condId) {
            const updatedCond = { ...cond, [key]: value };
            
            // Track and share category and brand selection
            if (key === 'value') {
              if (cond.field === 'category') {
                const selectedCategory = categories.find(cat => cat.name === value || cat.id === value);
                if (selectedCategory) {
                  setSharedData(prev => ({ 
                    ...prev, 
                    selectedCategory: selectedCategory 
                  }));
                }
              } else if (cond.field === 'brand') {
                const selectedBrand = brands.find(brand => brand.name === value || brand.id === value);
                if (selectedBrand) {
                  setSharedData(prev => ({ 
                    ...prev, 
                    selectedBrand: selectedBrand 
                  }));
                }
              }
            }
            
            return updatedCond;
          }
          return cond;
        })
      };
      
      // Always regenerate name when conditions change
      updatedCriteria.name = generateRewardCriteriaName(updatedCriteria);
      return updatedCriteria;
    }
    return criteria;
  });
  updateFormData('rewardCriteria', updatedCriteria);
};

  // Function to update criteria-level conditional operator
  const updateCriteriaConditionalOperator = (criteriaId, operator) => {
    const updatedCriteria = rewardCriteria.map(criteria => 
      criteria.id === criteriaId 
        ? { ...criteria, nextOperator: operator }
        : criteria
    );
    updateFormData('rewardCriteria', updatedCriteria);
  };

  /* const removeRewardCriteriaCondition = (criteriaId, condId) => {
    setRewardCriteria(prev => 
      prev.map(criteria => {
        if (criteria.id === criteriaId) {
          const updatedCriteria = {
            ...criteria,
            conditions: criteria.conditions.filter(cond => cond.id !== condId)
          };
          // Auto-generate name after removing condition
          if (!updatedCriteria.name || updatedCriteria.name === "" || updatedCriteria.name === "New Reward Criteria") {
            updatedCriteria.name = generateRewardCriteriaName(updatedCriteria);
          }
          return updatedCriteria;
        }
        return criteria;
      })
    );
  }; */


   const removeRewardCriteriaCondition = (criteriaId, condId) => {
    const updatedCriteria = rewardCriteria.map(criteria => {
      if (criteria.id === criteriaId) {
        const updatedCriteria = {
          ...criteria,
          conditions: criteria.conditions.filter(cond => cond.id !== condId)
        };
        // Auto-generate name after removing condition
        if (!updatedCriteria.name || updatedCriteria.name === "" || updatedCriteria.name === "New Reward Criteria") {
          updatedCriteria.name = generateRewardCriteriaName(updatedCriteria);
        }
        return updatedCriteria;
      }
      return criteria;
    });
    updateFormData('rewardCriteria', updatedCriteria);
  };


  /* const addRewardCriteria = () => {
    setRewardCriteria(prev => [...prev, { 
      id: uuidv4(), 
      name: "",
      fromDate: "",
      toDate: "", 
      conditions: [] 
    }]);
  }; */

  const addRewardCriteria = () => {
    const updatedCriteria = [...rewardCriteria, { 
      id: uuidv4(), 
      name: "",
      fromDate: "",
      toDate: "", 
      conditions: [] ,
      nextOperator: rewardCriteria.length > 0 ? "AND" : null // Add conditional operator for non-first criteria
    }];    updateFormData('rewardCriteria', updatedCriteria);
  };

  /* const removeRewardCriteria = (criteriaId) => {
    setRewardCriteria(prev => prev.filter(criteria => criteria.id !== criteriaId));
  }; */

  const removeRewardCriteria = (criteriaId) => {
    const updatedCriteria = rewardCriteria.filter(criteria => criteria.id !== criteriaId);
    updateFormData('rewardCriteria', updatedCriteria);
  };

  useEffect(() => {
  if (sharedData.selectedBrand && !sharedData.selectedCategory) {
    fetchCategoriesForBrand(sharedData.selectedBrand.id);
  }

  if (sharedData.selectedCategory && !sharedData.selectedBrand) {
    fetchBrandsForCategory(sharedData.selectedCategory.id);
  }

  if (sharedData.selectedCategory && sharedData.selectedBrand) {
    fetchItemsForSharing(sharedData.selectedCategory.id, sharedData.selectedBrand.id);
  }
}, [sharedData.selectedBrand, sharedData.selectedCategory]);


const fetchCategoriesForBrand = async (brandId) => {
  const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);
  try {
    setLoading(true);
    const res = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}product-category/get-productCategoriesBy-vendor_id?vendor_id=${brandId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-token': token
      }
    });

    const data = await res.json();
    const formatted = data.data.map(cat => ({
      id: cat._id,
      name: cat.product_category_name
    }));
    setCategories([{ id: "all", name: "All" }, ...formatted]);
  } catch (err) {
    console.error("Error fetching categories for brand", err);
  } finally {
    setLoading(false);
  }
};

const fetchBrandsForCategory = async (categoryId) => {
  const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);
  try {
    setLoading(true);
    const res = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}vendor/get-vendorsBy-productCategoryId?product_category_id=${categoryId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-token': token
      }
    });

    const data = await res.json();
    const formatted = data.data.map(brand => ({
      id: brand._id,
      name: brand.name
    }));
    setBrands([{ id: "all", name: "All" }, ...formatted]);
  } catch (err) {
    console.error("Error fetching brands for category", err);
  } finally {
    setLoading(false);
  }
};



  // Enhanced renderConditions function with conditional operators
  const renderConditions = (conditions, onUpdate, onRemove, parentId, onAdd) => (
    <div className="space-y-3">
      {conditions.map((cond, index) => {
        const selectedField = fields.find(f => f.value === cond.field);
        
        return (
          <div key={cond.id} className="space-y-3">
            {/* Conditional Operator Dropdown - Show for all conditions except the first */}
            {index > 0 && (
  <div className="flex justify-center">
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-1">
      <Select 
        value={conditions[index - 1]?.conditionalOperator || "AND"} 
        onValueChange={(value) => onUpdate(parentId, conditions[index - 1].id, "conditionalOperator", value)}
      >
        {conditionalOperators.map((op) => (
          <SelectItem key={op.value} value={op.value}>
            <div className="flex flex-col">
              <span className="font-medium">{op.label}</span>
            </div>
          </SelectItem>
        ))}
      </Select>
    </div>
  </div>
)}

            
            {/* Condition Row */}
            <div className="flex gap-3 items-start p-2 bg-gradient-to-r from-gray-50 to-gray-50/50 rounded-xl border border-gray-100 hover:border-gray-200 transition-all duration-200">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Field</label>
                  <Select 
                    value={cond.field} 
                    onValueChange={(value) => onUpdate(parentId, cond.id, "field", value)}
                    searchable={true}
                    placeholder="Select Field"
                  >
                    {fields.map((f) => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </Select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Operator</label>
                  <Select 
                    value={cond.operator} 
                    onValueChange={(value) => onUpdate(parentId, cond.id, "operator", value)}
                    searchable={true}
                    placeholder="Select Operator"
                  >
                    {operators.map((op) => (
                      <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                    ))}
                  </Select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Value</label>
                  {selectedField?.type === "multiselect" ? (
                    <MultiSelectDropdown
                      options={selectedField.options}
                      selectedValues={Array.isArray(cond.value) ? cond.value : (cond.value ? [cond.value] : [])}
                      onChange={(selectedValues) => onUpdate(parentId, cond.id, "value", selectedValues)}
                      placeholder={`Select ${selectedField.label}`}
                    />
                  ) : selectedField?.type === "select" ? (
                    <Select 
                      value={cond.value} 
                      onValueChange={(value) => onUpdate(parentId, cond.id, "value", value)}
                      searchable={true}
                      placeholder="Select Value"
                    >
                      {selectedField.options.map((option) => (
                        <SelectItem key={option.id} value={option.name}>
                          {option.name}
                        </SelectItem>
                      ))}
                    </Select>
                  ) : (
                    <Input
                      type={selectedField?.value === "amount" ? "number" : "text"}
                      value={cond.value}
                      onChange={(e) => onUpdate(parentId, cond.id, "value", e.target.value)}
                      placeholder="Enter value"
                    />
                  )}
                </div>
              </div>
              
              <div className="pt-6">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => onRemove(parentId, cond.id)}
                  className="w-8 h-8 p-0 rounded-lg"
                >
                  <FontAwesomeIcon icon={faTrash} className="text-xs" />
                </Button>
              </div>
            </div>
          </div>
        );
      })}    
      <Button 
        variant="outline"
        onClick={() => onAdd(parentId)} 
        disabled={loading}
        className="w-full py-1 border-dashed border-2 hover:border-[#f7525a] hover:text-[#f7525a] transition-all duration-200"
      >
        <FontAwesomeIcon icon={faPlus} className="mr-2" />
        {loading ? "Loading..." : "Add Condition"}
      </Button>
    </div>
  );


return (
  <Card className="w-full">
    <CardContent className="p-2">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className="text-md font-bold text-gray-900 mb-1">When do you want to give coins?</h3>
          {/* <p className="text-gray-600">Define the criteria and conditions for rewarding coins</p> */}
        </div>
        <Button onClick={addRewardCriteria} className="shadow-lg p-1">
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Add Criteria
        </Button>
      </div>
      
      <div className="space-y-6">
        {rewardCriteria.map((criteria, criteriaIndex) => (
          <div key={criteria.id}>
            {/* Conditional Operator Between Criteria - Show for all criteria except the first */}
            {criteriaIndex > 0 && (
              <div className="flex justify-center mb-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-1">
{/*                   <label className="block text-xs font-medium text-gray-600 mb-2 text-center">
                    Criteria Relationship
                  </label> */}
                  <Select 
                    value={rewardCriteria[criteriaIndex - 1]?.nextOperator || "AND"} 
                    onValueChange={(value) =>
          updateCriteriaConditionalOperator(rewardCriteria[criteriaIndex - 1].id, value)
        }
                  >
                    {conditionalOperators.map((op) => (
                      <SelectItem key={op.value} value={op.value}>
                        <div className="flex flex-col">
                          <span className="font-medium">{op.label}</span>
{/*                           <span className="text-xs text-gray-500">{op.description}</span>
 */}                        </div>
                      </SelectItem>
                    ))}
                  </Select>
                </div>
              </div>
            )}

            {/* Criteria Card */}
            <div className="p-6 border border-gray-200 rounded-lg bg-white shadow-sm hover:shadow-md transition-all duration-200">
              <div className="flex justify-between items-start mb-6">
                <div className="flex-1 mr-6">
{/*                   <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Rule Name
                  </label> */}
                  <Input
                    value={criteria.name || generateRewardCriteriaName(criteria)}
                    onChange={(e) => updateRewardCriteriaName(criteria.id, e.target.value)}
                    placeholder="Enter rule name or leave blank for auto-generation"
                    className="text-base"
                  />
                </div>
                {rewardCriteria.length > 1 && (
                  <Button 
                    variant="destructive"
                    size="sm"
                    onClick={() => removeRewardCriteria(criteria.id)}
                    className="mt-8"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                )}
              </div>
              
              {/* Date Range Section */}
              <div className="mb-6">
{/*                 <label className="block text-sm font-semibold text-gray-700 mb-4">
                  Date Range
                </label> */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                      From Date
                    </label>
                    <DateInput
                      value={criteria.fromDate}
                      onChange={(date) => updateRewardCriteriaFromDate(criteria.id, date)}
                      placeholder="DD/MM/YYYY"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                      To Date
                    </label>
                    <DateInput
                      value={criteria.toDate}
                      onChange={(date) => updateRewardCriteriaToDate(criteria.id, date)}
                      placeholder="DD/MM/YYYY"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-4">
                  Conditions
                </label>
                {renderConditions(
                  criteria.conditions,
                  updateRewardCriteriaCondition,
                  removeRewardCriteriaCondition,
                  criteria.id,
                  addRewardCriteriaCondition
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-between mt-4 mb-6">
          <Button variant="outline" onClick={onCancel} size="sm" className="px-4">
            Cancel
          </Button>
        <Button onClick={onNext} size="sm" className="px-4 shadow-lg">
          Next
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </CardContent>
  </Card>
);

};

// Reward Rules Substep Component - FIXED
const RewardRulesSubstep = ({ onNext,onCancel ,onBack,formData, updateFormData, mode  }) => {
  const { sharedData } = useRewardRuleContext();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([
    { id: "all", name: "All Items" },
    { id: "cement", name: "Cement" },
    { id: "steel", name: "Steel" },
    { id: "bricks", name: "Bricks" },
    { id: "sand", name: "Sand" },
    { id: "aggregates", name: "Aggregates" },
    { id: "tiles", name: "Tiles" },
    { id: "paint", name: "Paint" },
    { id: "electrical", name: "Electrical Items" },
    { id: "plumbing", name: "Plumbing Items" },
    { id: "hardware", name: "Hardware Items" }
  ]);

    // Add conditional operators for combining conditions
  const conditionalOperators = [
    { label: "AND", value: "AND", description: "All conditions must be true" },
    { label: "OR", value: "OR", description: "At least one condition must be true" },
    { label: "NOT", value: "NOT", description: "Condition must not be true" }
  ];

  // Use formData instead of local state
  const rewardRules = formData.rewardRules;

/*   const [rewardRules, setRewardRules] = useState([
    { 
      id: uuidv4(), 
      name: "", 
      rewardType: "fixed", 
      rewardValue: 0, 
      applyTarget: "order",
      destination: "wallet",
      limit: 0,
      applicableItems: []
    }
  ]); */

    //Fetch Items
const fetchItems = async (categoryId, brandId) => {
  if (!categoryId || !brandId) {
    setItems([]);
    return;
  }

  const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);
  try {
    setLoading(true);
    const response = await fetch(
      `${process.env.REACT_APP_FORE_BRICKBUCKET}product/get-products-by-category-and-vendor?product_category_id=${categoryId}&vendor_id=${brandId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('Items data:', data);
    
    const formattedItems = (data.data || []).map(item => ({
      id: item._id,
      name: item.product_name || item.name
    }));
    
    setItems(formattedItems);
  } catch (error) {
    console.error('Error fetching items:', error);
    setItems([]);
  } finally {
    setLoading(false);
  }
};



    // Use shared items from RewardCriteriaSubstep
  const availableItems = sharedData.fetchedItems.length > 0 
    ? sharedData.fetchedItems 
    : [{ id: "all", name: "All Items" }]; // Fallback

const generateRewardRuleName = (rule) => {
  if (!rule.rewardType || (!rule.rewardValue && rule.rewardValue !== 0)) {
    return "New Reward Rule";
  }

  // Convert rewardValue to number if it's a string
  const rewardValue = typeof rule.rewardValue === 'string' ? parseFloat(rule.rewardValue) : rule.rewardValue;

  // Format reward amount based on reward type
  let rewardAmount = "";
  if (rule.rewardType === "fixed") {
    rewardAmount = `${rewardValue} coins`;
  } else if (rule.rewardType === "percentage") {
    rewardAmount = `${rewardValue}% coins`;
  }

  // Format what it's applied on
  let appliedOn = "";
  if (rule.applyTarget === "order") {
    appliedOn = "order value";
  } else if (rule.applyTarget === "item") {
    if (rule.applicableItems && rule.applicableItems.length > 0) {
      // Filter out "all" items if present
      const validItems = rule.applicableItems.filter(item =>
        item && (typeof item === 'object' ? item.id !== "all" : item !== "all")
      );
      
      if (validItems.length === 0) {
        appliedOn = "all items";
      } else if (validItems.length === 1) {
        const itemName = typeof validItems[0] === 'object' ? validItems[0].name : validItems[0];
        appliedOn = itemName || 'selected item';
      } else if (validItems.length === 2) {
        const item1Name = typeof validItems[0] === 'object' ? validItems[0].name : validItems[0];
        const item2Name = typeof validItems[1] === 'object' ? validItems[1].name : validItems[1];
        appliedOn = `${item1Name || 'item'} and ${item2Name || 'item'}`;
      } else {
        const item1Name = typeof validItems[0] === 'object' ? validItems[0].name : validItems[0];
        const item2Name = typeof validItems[1] === 'object' ? validItems[1].name : validItems[1];
        appliedOn = `${item1Name || 'item'}, ${item2Name || 'item'} and ${validItems.length - 2} more items`;
      }
    } else {
      appliedOn = "selected items";
    }
  }

  // Format destination
  let destination = "";
  if (rule.destination === "wallet") {
    destination = "wallet";
  } else if (rule.destination === "immediate") {
    destination = "immediate order discount";
  }

  // Format limit based on limit type
  let limitText = "";
  if (rule.limit && parseFloat(rule.limit) > 0) {
    const limitValue = parseFloat(rule.limit);
    
    if (rule.limitType === "fixed") {
      limitText = ` (max ${limitValue} coins per order)`;
    } else if (rule.limitType === "percentage") {
      if (rule.applyTarget === "order") {
        limitText = ` (max ${limitValue}% of order value)`;
      } else {
        limitText = ` (max ${limitValue}% of item value)`;
      }
    }
  }

  // Construct the final name
  let ruleName = "";
  
  if (rule.applyTarget === "order") {
    ruleName = `Give ${rewardAmount} on ${appliedOn} to ${destination}${limitText}`;
  } else if (rule.applyTarget === "item") {
    ruleName = `Give ${rewardAmount} on ${appliedOn} to ${destination}${limitText}`;
  } else {
    // Fallback for undefined applyTarget
    ruleName = `Give ${rewardAmount} to ${destination}${limitText}`;
  }

  return ruleName;
};



/*   const updateRewardRule = (ruleId, key, value) => {
    setRewardRules(prev => 
      prev.map(rule => {
        if (rule.id === ruleId) {
          const updatedRule = { ...rule, [key]: value };
          if (!updatedRule.name || updatedRule.name === "") {
            updatedRule.name = generateRewardRuleName(updatedRule);
          }
          return updatedRule;
        }
        return rule;
      })
    );
  }; */

 const updateRewardRule = (ruleId, key, value) => {
  const updatedRules = rewardRules.map(rule => {
    if (rule.id === ruleId) {
      const updatedRule = { ...rule, [key]: value };
      // Always regenerate name when any field changes (except when explicitly setting the name)
      if (key !== 'name') {
        updatedRule.name = generateRewardRuleName(updatedRule);
      }
      return updatedRule;
    }
    return rule;
  });
  updateFormData('rewardRules', updatedRules);
};


/*   const updateRewardRuleApplicableItems = (ruleId, selectedValues) => {
    const selectedItems = selectedValues.map(itemId => {
      const item = availableItems.find(item => item.id === itemId);
      return item ? { id: item.id, name: item.name } : null;
    }).filter(Boolean);

    setRewardRules(prev => 
      prev.map(rule => 
        rule.id === ruleId ? { ...rule, applicableItems: selectedItems } : rule
      )
    );
  }; */

   const updateRewardRuleApplicableItems = (ruleId, selectedValues) => {
  // FIXED: Preserve user-edited values when deselecting items
  const currentRule = rewardRules.find(r => r.id === ruleId);
  const existingItems = currentRule?.applicableItems || [];
  
  const selectedItems = selectedValues.map(itemId => {
    const item = availableItems.find(item => item.id === itemId);
    if (!item) return null;
    
    // Preserve existing entity if it already has user-edited data
    const existingEntity = existingItems.find(e => e.id === itemId);
    return existingEntity || { id: item.id, name: item.name };
  }).filter(Boolean);

  const updatedRules = rewardRules.map(rule => {
    if (rule.id === ruleId) {
      const updatedRule = { ...rule, applicableItems: selectedItems };
      // Regenerate name when applicable items change
      updatedRule.name = generateRewardRuleName(updatedRule);
      return updatedRule;
    }
    return rule;
  });
  updateFormData('rewardRules', updatedRules);
};

  // Function to update reward-level conditional operator
  const updateRewardRulesConditionalOperator = (ruleId, operator) => {
    const updatedRules = rewardRules.map(rule => 
      rule.id === ruleId 
        ? { ...rule, nextOperator: operator }
        : rule
    );
    updateFormData('rewardRules', updatedRules);
  };

/*   const addRewardRule = () => {
    setRewardRules(prev => [...prev, { 
      id: uuidv4(), 
      name: "", 
      rewardType: "fixed", 
      rewardValue: 0, 
      applyTarget: "order",
      destination: "wallet",
      limit: 0,
      applicableItems: [] 
    }]);
  }; */

  const addRewardRule = () => {
    const updatedRules = [...rewardRules, { 
      id: uuidv4(), 
      name: "", 
      rewardType: "fixed", 
      rewardValue: 0, 
      applyTarget: "order",
      destination: "wallet",
      limit: 0,
      limitType: "fixed",
      applicableItems: [],
      nextOperator:rewardRules.length > 0 ? "AND" : null // Add conditional operator for non-first conditions
    }];
    updateFormData('rewardRules', updatedRules);
  };

  /* const removeRewardRule = (ruleId) => {
    setRewardRules(prev => prev.filter(rule => rule.id !== ruleId));
  }; */

  const removeRewardRule = (ruleId) => {
    const updatedRules = rewardRules.filter(rule => rule.id !== ruleId);
    updateFormData('rewardRules', updatedRules);
  };

  return (
    <Card className="w-full">
      <CardContent className="p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h3 className="text-md font-bold text-gray-900 mb-1">How many coins can be given?</h3>
{/*             <p className="text-gray-600">Configure the reward amounts and limits</p>
 */}            {/* Show selected category and brand info */}
            {sharedData.selectedCategory && sharedData.selectedBrand && (
              <div className="mt-2 p-2 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  Items loaded for: <strong>{sharedData.selectedCategory.name}</strong> - <strong>{sharedData.selectedBrand.name}</strong>
                  ({availableItems.length - 1} items available)
                </p>
              </div>
            )}
          </div>
          <Button onClick={addRewardRule} className="shadow-lg">
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Add Reward Rule
          </Button>
        </div>
        
        <div className="space-y-6">
          {rewardRules.map((rule,ruleIndex) => (
            <div key={rule.id} >
              {ruleIndex > 0 && (
              <div className="flex justify-center mb-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-1">
{/*                   <label className="block text-xs font-medium text-gray-600 mb-2 text-center">
                    Criteria Relationship
                  </label> */}
                  <Select 
                    value={rewardRules[ruleIndex-1]?.nextOperator || "AND"} 
                    onValueChange={(value) => updateRewardRulesConditionalOperator(rewardRules[ruleIndex - 1].id, value)}
                  >
                    {conditionalOperators.map((op) => (
                      <SelectItem key={op.value} value={op.value}>
                        <div className="flex flex-col">
                          <span className="font-medium">{op.label}</span>
{/*                           <span className="text-xs text-gray-500">{op.description}</span>
 */}                        </div>
                      </SelectItem>
                    ))}
                  </Select>
                </div>
              </div>
            )}

            <div className="p-6 border border-gray-200 rounded-lg bg-white shadow-sm hover:shadow-md transition-all duration-200">
              <div className="flex justify-between items-start mb-6">
                <div className="flex-1 mr-6">
{/*                   <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Rule Name
                  </label> */}
                  <Input
                    value={rule.name || generateRewardRuleName(rule)}
                    onChange={(e) => updateRewardRule(rule.id, "name", e.target.value)}
                    placeholder="Enter rule name or leave blank for auto-generation"
                    className="text-base"
                  />
                </div>
                {rewardRules.length > 1 && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => removeRewardRule(rule.id)}
                    className="mt-8"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                )}
              </div>
              
              <div className="space-y-6">
                <div>
{/*                   <label className="block text-sm font-semibold text-gray-700 mb-4">
                    Reward Configuration
                  </label> */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-2">
                        Reward Type
                      </label>
                      {/* FIXED: Use new Select component structure */}
                      <Select 
                        value={rule.rewardType} 
                        onValueChange={(value) => updateRewardRule(rule.id, "rewardType", value)}
                        placeholder="Select Reward Type"
                      >
                        <SelectItem value="fixed">Fixed</SelectItem>
                        <SelectItem value="percentage">Percentage</SelectItem>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-2">
                        Reward Value
                      </label>
                      <Input
                        type="number"
                        placeholder="Enter amount"
                        value={rule.rewardValue}
                        onChange={(e) => updateRewardRule(rule.id, "rewardValue", e.target.value)}
                      />
                    </div>
                                      <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                      Rewarded On
                    </label>
                    {/* FIXED: Use new Select component structure */}
                    <Select 
                      value={rule.applyTarget} 
                      onValueChange={(value) => updateRewardRule(rule.id, "applyTarget", value)}
                      placeholder="Select Apply Target"
                    >
                      <SelectItem value="order">Order</SelectItem>
                      <SelectItem value="item">Item</SelectItem>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                      Rewarded To
                    </label>
                    {/* FIXED: Use new Select component structure */}
                    <Select 
                      value={rule.destination} 
                      onValueChange={(value) => updateRewardRule(rule.id, "destination", value)}
                      placeholder="Select Destination"
                    >
                      <SelectItem value="wallet">Wallet</SelectItem>
                      <SelectItem value="immediate">Immediate Order</SelectItem>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                      Rewarded Items
                    </label>
                    <MultiSelectDropdown
                      options={availableItems}
                      selectedValues={rule.applicableItems ? 
                        rule.applicableItems.map(item => 
                          typeof item === 'object' ? item.id : item
                        ).filter(id => id !== "all") : []
                      }
                      onChange={(selectedValues) => updateRewardRuleApplicableItems(rule.id, selectedValues)}
                      placeholder={availableItems.length > 1 ? "Select items" : "Select category & brand in previous step"}
                    />
                    {availableItems.length <= 1 && (
                      <p className="text-xs text-gray-500 mt-1">
                        Items will appear after selecting category and brand in the previous step
                      </p>
                    )}
                  </div>

                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-2">
                        Limit
                      </label>
                      <Input
                        type="number"
                        placeholder="Max limit"
                        value={rule.limit}
                        onChange={(e) => updateRewardRule(rule.id, "limit", e.target.value)}
                      />
                    </div>
                    <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">Limit Type</label>
                    <Select 
                        value={rule?.limitType}
                      onChange={(value) => updateRewardRule(rule.id, 'limitType', value)}
                        placeholder="Select Limit Type"
                      >
                        <SelectItem value="">Select Limit Type</SelectItem>
                        <SelectItem value="fixed">Fixed</SelectItem>
                        <SelectItem value="percentage">Percentage</SelectItem>
                      </Select>
                  </div>
                </div>
              </div>
            </div>
            </div>
          ))}
        </div>

        <div className="flex justify-between mt-8 mb-6">
          <Button variant="outline" onClick={onCancel} size="sm" className="px-4">
            Cancel
          </Button>
          <div className='space-x-2'>
          <Button variant="outline" onClick={onBack} size="sm" className="px-4">
            Back
          </Button>
          <Button onClick={onNext} size="sm" className="px-4 shadow-lg">
            Next
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};


// Applicability Rules Substep Component
const ApplicabilityRulesSubstep = ({ onNext,onCancel, onBack,formData, updateFormData, mode  }) => {
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
const [filteredBrands, setFilteredBrands] = useState([]);

  const [locations, setLocations] = useState([
    { id: "all", name: "All" },
    { id: "hyderabad", name: "Hyderabad" },
    { id: "vijayawada", name: "Vijayawada" },
    { id: "guntur", name: "Guntur" },
    { id: "visakhapatnam", name: "Visakhapatnam" }
  ]);

const customerTypes = [
  {id:"Individual Buyer",name:"Individual Buyer"},
  {id:"Home Builder",name:"Home Builder"},
  {id:"Pro Builder",name:"Pro Builder"},
  {id :"Contractor",name:"Contractor"}


]

useEffect(() => {
  const selectedCategoryName = getSelectedValue('category');
  const selectedBrandName = getSelectedValue('brand');

  const selectedCategory = categories.find(c => c.name === selectedCategoryName);
  const selectedBrand = brands.find(b => b.name === selectedBrandName);

  if (selectedCategory && selectedBrand) {
    // Optional: If you want to load items for this combo
    // fetchItemsByCategoryAndBrand(selectedCategory.id, selectedBrand.id);
  }

  if (selectedCategory && !selectedBrand) {
    filterBrandsByCategory(selectedCategory.id);
  } else if (!selectedCategory && selectedBrand) {
    filterCategoriesByBrand(selectedBrand.id);
  } else {
    setFilteredCategories(categories);
    setFilteredBrands(brands);
  }
}, [formData.applicabilityRules, categories, brands]);


const getSelectedValue = (fieldKey) => {
  const allConditions = formData.applicabilityRules.flatMap(rule => rule.conditions);
  const found = [...allConditions].reverse().find(cond => cond.field === fieldKey && cond.value);
  return found?.value || null;
};



const filterBrandsByCategory = async (categoryId) => {
  const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);
  try {
    const res = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}vendor/get-vendorsBy-productCategoryId?product_category_id=${categoryId}`, {
      headers: { 'x-token': token }
    });
    const data = await res.json();
    const formatted = data.data.map(b => ({ id: b._id, name: b.name }));
    setFilteredBrands(formatted);
  } catch (err) {
    console.error('Error filtering brands by category:', err);
  }
};

const filterCategoriesByBrand = async (brandId) => {
  const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);
  try {
    const res = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}product-category/get-productCategoriesBy-vendor_id?vendor_id=${brandId}`, {
      headers: { 'x-token': token }
    });
    const data = await res.json();
    const formatted = data.data.map(c => ({ id: c._id, name: c.product_category_name }));
    setFilteredCategories(formatted);
  } catch (err) {
    console.error('Error filtering categories by brand:', err);
  }
};


  const fields = [
    { label: "Category", value: "category", type: "select", options: filteredCategories.length > 0 ? filteredCategories : categories },
  { label: "Brand", value: "brand", type: "select", options: filteredBrands.length > 0 ? filteredBrands : brands },
    { label: "Amount", value: "amount" },
    { label: "Is Ecommerce", value: "is_ecommerce", type: "select", options: [
      { id: "true", name: "Yes" },
      { id: "false", name: "No" }
    ]},
    { label: "Campaign", value: "campaign" },
    { label: "Event", value: "event" },
    { label: "Location", value: "location", type: "multiselect", options: locations },
    { label: "Items", value: "items", type: "multiselect", options: items },
    { label: "Buyer Type", value:"customer_type", type:"select", options: customerTypes}
  ];

  const operators = [
  { label: "is", value: "==" },
  { label: "is not", value: "!=" },
  { label: "is greater than", value: ">" },
  { label: "is greater than or equal to", value: ">=" },
  { label: "is less than", value: "<" },
  { label: "is less than or equal to", value: "<=" },
  { label: "contains", value: "contains" },
  { label: "does not contain", value: "not_contains" },
  { label: "starts with", value: "starts_with" },
  { label: "ends with", value: "ends_with" },
  { label: "is empty", value: "is_empty" },
  { label: "is not empty", value: "is_not_empty" },
  { label: "exists", value: "exists" },
  { label: "does not exist", value: "not_exists" },
  { label: "is in", value: "in" },
  { label: "is not in", value: "not_in" },
  { label: "includes any of", value: "includes_any" },
  { label: "includes all of", value: "includes_all" },
  { label: "is before", value: "before" },
  { label: "is after", value: "after" },
  { label: "is between", value: "between" },
  { label: "matches pattern", value: "regex" },
  { label: "is like", value: "like" }
];

  // Add conditional operators for combining conditions
  const conditionalOperators = [
    { label: "AND", value: "AND", description: "All conditions must be true" },
    { label: "OR", value: "OR", description: "At least one condition must be true" },
    { label: "NOT", value: "NOT", description: "Condition must not be true" }
  ];
  
  // Use formData instead of local state
  const applicabilityRules = formData.applicabilityRules;

  /* const [applicabilityRules, setApplicabilityRules] = useState([
    { id: uuidv4(), name: "", conditions: [] }
  ]); */

  // Add useEffect to fetch data
  useEffect(() => {
    fetchCategories();
    fetchBrands();
    fetchLocations();
  }, []);

  // Fetch Categories
  const fetchCategories = async () => {
    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}product-category/get-all-productCategories`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const formattedCategories = (data.data).map(cat => ({
        id: cat._id,
        name: cat.product_category_name
      }));
      setCategories(formattedCategories); 
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Brands
  const fetchBrands = async () => {
    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}vendor/get-all-vendors`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const formattedBrands = (data.data).map(brand => ({
        id: brand._id,
        name: brand.name
      }));
      setBrands(formattedBrands); 
    } catch (error) {
      console.error('Error fetching brands:', error);
      setBrands([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Locations
  const fetchLocations = async () => {
    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}hub/get-all-hubs`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const formattedLocations = [
        { id: "all", name: "All" },
        ...(data.data || []).map(location => ({
          id: location._id,
          name: location.city || location.name || location.hub_name
        }))
      ];
      
      setLocations(formattedLocations);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setLocations([
        { id: "all", name: "All" },
        { id: "hyderabad", name: "Hyderabad" },
        { id: "vijayawada", name: "Vijayawada" },
        { id: "guntur", name: "Guntur" },
        { id: "visakhapatnam", name: "Visakhapatnam" }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const generateApplicabilityRuleName = (rule) => {
  if (!rule.conditions || rule.conditions.length === 0) {
    return "New Applicability Rule";
  }
  
  const conditionStrings = rule.conditions.map(cond => {
    if (!cond.field || !cond.operator || (!cond.value && cond.value !== false && cond.value !== 0)) return "";
    
    const fieldLabel = fields.find(f => f.value === cond.field)?.label || cond.field;
    const operatorLabel = operators.find(op => op.value === cond.operator)?.label || cond.operator;
    
    let valueString = "";
    if (cond.field === "location" && Array.isArray(cond.value)) {
      const locationNames = cond.value.map(locId => {
        const location = locations.find(loc => loc.id === locId);
        return location ? location.name : locId;
      });
      valueString = locationNames.length > 2 
        ? `${locationNames.slice(0, 2).join(", ")} and ${locationNames.length - 2} more` 
        : locationNames.join(", ");
    } else if (cond.field === "items" && Array.isArray(cond.value)) {
      const itemNames = cond.value.map(itemId => {
        const item = items.find(i => i.id === itemId);
        return item ? item.name : itemId;
      });
      valueString = itemNames.length > 2 
        ? `${itemNames.slice(0, 2).join(", ")} and ${itemNames.length - 2} more` 
        : itemNames.join(", ");
    } else if (cond.field === "amount") {
      valueString = `₹${cond.value}`;
    } else if (cond.field === "is_ecommerce") {
      valueString = cond.value === "Yes" ? "Yes" : "No";
    } else if (cond.field === "category") {
      valueString = cond.value;
    } else if (cond.field === "brand") {
      valueString = cond.value;
    } else if (cond.field === "campaign") {
      valueString = cond.value;
    } else if (cond.field === "event") {
      valueString = cond.value;
    } else {
      valueString = cond.value.toString();
    }
    
    return `${fieldLabel} ${operatorLabel} ${valueString}`;
  }).filter(str => str !== "");
  
  if (conditionStrings.length === 0) {
    return "New Applicability Rule";
  }
  
  // Create the applicability statement
  let applicabilityStatement = "";
  if (conditionStrings.length === 1) {
    applicabilityStatement = `Coins can be applied when ${conditionStrings[0]}`;
  } else if (conditionStrings.length === 2) {
    applicabilityStatement = `Coins can be applied when ${conditionStrings[0]} and ${conditionStrings[1]}`;
  } else if (conditionStrings.length === 3) {
    applicabilityStatement = `Coins can be applied when ${conditionStrings[0]}, ${conditionStrings[1]} and ${conditionStrings[2]}`;
  } else {
    applicabilityStatement = `Coins can be applied when ${conditionStrings[0]}, ${conditionStrings[1]} and ${conditionStrings.length - 2} more conditions are met`;
  }
  
  return applicabilityStatement;
};

  /* const addApplicabilityCondition = (ruleId) => {
    setApplicabilityRules(prev => 
      prev.map(rule => 
        rule.id === ruleId 
          ? {
              ...rule,
              conditions: [...rule.conditions, { 
                id: uuidv4(), 
                field: "", 
                operator: "==", 
                value: "" 
              }]
            }
          : rule
      )
    );
  }; */


  const addApplicabilityCondition = (ruleId) => {
    const updatedRules = applicabilityRules.map(rule => 
      rule.id === ruleId 
        ? {
            ...rule,
            conditions: [...rule.conditions, { 
              id: uuidv4(), 
              field: "", 
              operator: "==", 
              value: "" ,
              conditionalOperator: rule.conditions.length > 0 ? "AND" : null // Add conditional operator for non-first conditions
            }]
          }
        : rule
    );
    updateFormData('applicabilityRules', updatedRules);
  };

 /*  const updateApplicabilityCondition = (ruleId, condId, key, value) => {
    setApplicabilityRules(prev => 
      prev.map(rule => {
        if (rule.id === ruleId) {
          const updatedRule = {
            ...rule,
            conditions: rule.conditions.map(cond => 
              cond.id === condId ? { ...cond, [key]: value } : cond
            )
          };
          if (!updatedRule.name || updatedRule.name === "") {
            updatedRule.name = generateApplicabilityRuleName(updatedRule);
          }
          return updatedRule;
        }
        return rule;
      })
    );
  }; */

const updateApplicabilityCondition = (ruleId, condId, key, value) => {
  const updatedRules = applicabilityRules.map(rule => {
    if (rule.id === ruleId) {
      const updatedRule = {
        ...rule,
        conditions: rule.conditions.map(cond => 
          cond.id === condId ? { ...cond, [key]: value } : cond
        )
      };
      // Always regenerate name when conditions change
      updatedRule.name = generateApplicabilityRuleName(updatedRule);
      return updatedRule;
    }
    return rule;
  });
  updateFormData('applicabilityRules', updatedRules);
};


  // Function to update criteria-level conditional operator
  const updateApplicabilityConditionalOperator = (ruleId, operator) => {
    const updatedRule = applicabilityRules.map(rule => 
      rule.id === ruleId 
        ? { ...rule, nextOperator: operator }
        : rule
    );
    updateFormData('applicabilityRules', updatedRule);
  };

  /* const removeApplicabilityCondition = (ruleId, condId) => {
    setApplicabilityRules(prev => 
      prev.map(rule => 
        rule.id === ruleId 
          ? {
              ...rule,
              conditions: rule.conditions.filter(cond => cond.id !== condId)
            }
          : rule
      )
    );
  }; */

const removeApplicabilityCondition = (ruleId, condId) => {
    const updatedRules = applicabilityRules.map(rule => 
      rule.id === ruleId 
        ? {
            ...rule,
            conditions: rule.conditions.filter(cond => cond.id !== condId)
          }
        : rule
    );
    updateFormData('applicabilityRules', updatedRules);
  };


 /*  const addApplicabilityRule = () => {
    setApplicabilityRules(prev => [...prev, { 
      id: uuidv4(), 
      name: "", 
      conditions: [] 
    }]);
  };
 */
  
  const addApplicabilityRule = () => {
    const updatedRules = [...applicabilityRules, { 
      id: uuidv4(), 
      name: "", 
      conditions: [],
      nextOperator: applicabilityRules.length > 0 ? "AND" : null
    }];
    updateFormData('applicabilityRules', updatedRules);
  };

  /* const updateApplicabilityRuleName = (ruleId, name) => {
    setApplicabilityRules(prev => 
      prev.map(rule => 
        rule.id === ruleId ? { ...rule, name } : rule
      )
    );
  }; */

const updateApplicabilityRuleName = (ruleId, name) => {
  const updatedRules = applicabilityRules.map(rule => 
    rule.id === ruleId ? { ...rule, name, isCustomName: true } : rule
  );
  updateFormData('applicabilityRules', updatedRules);
};


  /* const removeApplicabilityRule = (ruleId) => {
    setApplicabilityRules(prev => prev.filter(rule => rule.id !== ruleId));
  }; */

  const removeApplicabilityRule = (ruleId) => {
    const updatedRules = applicabilityRules.filter(rule => rule.id !== ruleId);
    updateFormData('applicabilityRules', updatedRules);
  };

// Enhanced renderConditions function with conditional operators
  const renderConditions = (conditions, onUpdate, onRemove, parentId, onAdd) => (
    <div className="space-y-3">
      {conditions.map((cond, index) => {
        const selectedField = fields.find(f => f.value === cond.field);
        
        return (
          <div key={cond.id} className="space-y-3">
            {/* Conditional Operator Dropdown - Show for all conditions except the first */}
            {index > 0 && (
  <div className="flex justify-center">
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-1">
      <Select 
        value={conditions[index - 1]?.conditionalOperator || "AND"} 
        onValueChange={(value) => onUpdate(parentId, conditions[index - 1].id, "conditionalOperator", value)}
      >
        {conditionalOperators.map((op) => (
          <SelectItem key={op.value} value={op.value}>
            <div className="flex flex-col">
              <span className="font-medium">{op.label}</span>
            </div>
          </SelectItem>
        ))}
      </Select>
    </div>
  </div>
)}

            
            {/* Condition Row */}
            <div className="flex gap-3 items-start p-2 bg-gradient-to-r from-gray-50 to-gray-50/50 rounded-xl border border-gray-100 hover:border-gray-200 transition-all duration-200">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Field</label>
                  <Select 
                    value={cond.field} 
                    onValueChange={(value) => onUpdate(parentId, cond.id, "field", value)}
                    searchable={true}
                    placeholder="Select Field"
                  >
                    {fields.map((f) => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </Select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Operator</label>
                  <Select 
                    value={cond.operator} 
                    onValueChange={(value) => onUpdate(parentId, cond.id, "operator", value)}
                    searchable={true}
                    placeholder="Select Operator"
                  >
                    {operators.map((op) => (
                      <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                    ))}
                  </Select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Value</label>
                  {selectedField?.type === "multiselect" ? (
                    <MultiSelectDropdown
                      options={selectedField.options}
                      selectedValues={Array.isArray(cond.value) ? cond.value : (cond.value ? [cond.value] : [])}
                      onChange={(selectedValues) => onUpdate(parentId, cond.id, "value", selectedValues)}
                      placeholder={`Select ${selectedField.label}`}
                    />
                  ) : selectedField?.type === "select" ? (
                    <Select 
                      value={cond.value} 
                      onValueChange={(value) => onUpdate(parentId, cond.id, "value", value)}
                      searchable={true}
                      placeholder="Select Value"
                    >
                      {selectedField.options.map((option) => (
                        <SelectItem key={option.id} value={option.name}>
                          {option.name}
                        </SelectItem>
                      ))}
                    </Select>
                  ) : (
                    <Input
                      type={selectedField?.value === "amount" ? "number" : "text"}
                      value={cond.value}
                      onChange={(e) => onUpdate(parentId, cond.id, "value", e.target.value)}
                      placeholder="Enter value"
                    />
                  )}
                </div>
              </div>
              
              <div className="pt-6">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => onRemove(parentId, cond.id)}
                  className="w-8 h-8 p-0 rounded-lg"
                >
                  <FontAwesomeIcon icon={faTrash} className="text-xs" />
                </Button>
              </div>
            </div>
          </div>
        );
      })}    
      <Button 
        variant="outline"
        onClick={() => onAdd(parentId)} 
        disabled={loading}
        className="w-full py-1 border-dashed border-2 hover:border-[#f7525a] hover:text-[#f7525a] transition-all duration-200"
      >
        <FontAwesomeIcon icon={faPlus} className="mr-2" />
        {loading ? "Loading..." : "Add Condition"}
      </Button>
    </div>
  );
      


   return (
    <Card className="w-full">
      <CardContent className="p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h3 className="text-md font-bold text-gray-900 mb-1">When can these coins be applied?</h3>
{/*             <p className="text-gray-600">Define the applicability conditions for using coins</p>
 */}          </div>
          <Button onClick={addApplicabilityRule} className="shadow-lg">
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Add Applicability Rule
          </Button>
        </div>
        
        <div className="space-y-6">
          {applicabilityRules.map((rule, ruleIndex) => (
            <div key={rule.id}>
                          {ruleIndex > 0 && (
              <div className="flex justify-center mb-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-1">
{/*                   <label className="block text-xs font-medium text-gray-600 mb-2 text-center">
                    Criteria Relationship
                  </label> */}
                  <Select 
                    value={applicabilityRules[ruleIndex-1]?.nextOperator || "AND"} 
                    onValueChange={(value) => updateApplicabilityConditionalOperator(applicabilityRules[ruleIndex - 1].id, value)}
                  >
                    {conditionalOperators.map((op) => (
                      <SelectItem key={op.value} value={op.value}>
                        <div className="flex flex-col">
                          <span className="font-medium">{op.label}</span>
{/*                           <span className="text-xs text-gray-500">{op.description}</span>
 */}                        </div>
                      </SelectItem>
                    ))}
                  </Select>
                </div>
              </div>
            )}

            <div className="p-6 border border-gray-200 rounded-lg bg-white shadow-sm hover:shadow-md transition-all duration-200">
              <div className="flex justify-between items-start mb-6">
                <div className="flex-1 mr-6">
                  {/* <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Rule Name
                  </label> */}
                  <Input
                    value={rule.name || generateApplicabilityRuleName(rule)}
                    onChange={(e) => updateApplicabilityRuleName(rule.id, e.target.value)}
                    placeholder="Enter rule name or leave blank for auto-generation"
                     className="text-base"
                  />
                </div>
                {applicabilityRules.length > 1 && (
                  <Button 
                    variant="destructive"
                    size="sm"
                    onClick={() => removeApplicabilityRule(rule.id)}
                    className="mt-8"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-4">
                  Conditions
                </label>
                {renderConditions(
                  rule.conditions,
                  updateApplicabilityCondition,
                  removeApplicabilityCondition,
                  rule.id,
                  addApplicabilityCondition
                )}
              </div>
            </div>
            </div>
          ))}
        </div>

        <div className="flex justify-between mt-8 mb-6">
          <Button variant="outline" onClick={onCancel} size="sm" className="px-4">
            Cancel
          </Button>
          <div className='space-x-2'>
          <Button variant="outline" onClick={onBack} size="sm" className="px-4">
            Back
          </Button>
          <Button onClick={onNext} size="sm" className="px-4 shadow-lg">
            Next
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Redemption Rules Substep Component
const RedemptionRulesSubstep = ({ onNext,onCancel, onBack, formData, updateFormData, mode  }) => {
  /* const [redemptionRules, setRedemptionRules] = useState([
    { 
      id: uuidv4(), 
      name: "", 
      redeemType: "percentage", 
      redeemValue: 0,
      applyTarget: "order"
    }
  ]); */

    // Add conditional operators for combining conditions
  const conditionalOperators = [
    { label: "AND", value: "AND", description: "All conditions must be true" },
    { label: "OR", value: "OR", description: "At least one condition must be true" },
    { label: "NOT", value: "NOT", description: "Condition must not be true" }
  ];
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);


    // Add useEffect to fetch data
  useEffect(() => {
    fetchCategories();
  }, []);

        // Fetch Categories
  const fetchCategories = async () => {
    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}product-category/get-all-productCategories`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(data)
      const formattedCategories = (data.data).map(cat => ({
        id:cat._id,
        name:cat.product_category_name
      }));
      setCategories(formattedCategories); 
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

      // Use shared items from RewardCriteriaSubstep
  const availableItems = categories.length > 0 
    ? categories 
    : [{ id: "all", name: "All Items" }]; // Fallback

  // Use formData instead of local state
  const redemptionRules = formData.redemptionRules;

const generateRedemptionRuleName = (rule) => {
  if (!rule.redeemType || (!rule.redeemValue && rule.redeemValue !== 0)) {
    return "New Redemption Rule";
  }

  // Convert redeemValue to number if it's a string
  const redeemValue = typeof rule.redeemValue === 'string' ? parseFloat(rule.redeemValue) : rule.redeemValue;

  // Format redeem amount based on redeem type
  let redeemAmount = "";
  if (rule.redeemType === "fixed") {
    redeemAmount = `${redeemValue} coins`;
  } else if (rule.redeemType === "percentage") {
    redeemAmount = `${redeemValue}% coins`;
  }

  // Format what it's applied on
  let appliedOn = "";
  if (rule.applyTarget === "order") {
    appliedOn = "order value";
  } else if (rule.applyTarget === "item") {
    if (rule.applicableItems && rule.applicableItems.length > 0) {
      // Filter out "all" items if present
      const validItems = rule.applicableItems.filter(item =>
        item && (typeof item === 'object' ? item.id !== "all" : item !== "all")
      );
      
      if (validItems.length === 0) {
        appliedOn = "all items";
      } else if (validItems.length === 1) {
        const itemName = typeof validItems[0] === 'object' ? validItems[0].name : validItems[0];
        appliedOn = itemName || 'selected item';
      } else if (validItems.length === 2) {
        const item1Name = typeof validItems[0] === 'object' ? validItems[0].name : validItems[0];
        const item2Name = typeof validItems[1] === 'object' ? validItems[1].name : validItems[1];
        appliedOn = `${item1Name || 'item'} and ${item2Name || 'item'}`;
      } else {
        const item1Name = typeof validItems[0] === 'object' ? validItems[0].name : validItems[0];
        const item2Name = typeof validItems[1] === 'object' ? validItems[1].name : validItems[1];
        appliedOn = `${item1Name || 'item'}, ${item2Name || 'item'} and ${validItems.length - 2} more items`;
      }
    } else {
      appliedOn = "selected items";
    }
  }

  // Format redemption source
  let source = "";
  if (rule.source === "wallet") {
    source = "wallet";
  } else if (rule.source === "earned") {
    source = "earned coins";
  }

  // Format limit based on limit type
  let limitText = "";
  if (rule.limit && parseFloat(rule.limit) > 0) {
    const limitValue = parseFloat(rule.limit);
    
    if (rule.limitType === "fixed") {
      limitText = ` (max ${limitValue} coins per order)`;
    } else if (rule.limitType === "percentage") {
      if (rule.applyTarget === "order") {
        limitText = ` (max ${limitValue}% of order value)`;
      } else {
        limitText = ` (max ${limitValue}% of item value)`;
      }
    }
  }

  // Format minimum redemption requirement
  let minRedemptionText = "";
  if (rule.minRedemption && parseFloat(rule.minRedemption) > 0) {
    const minValue = parseFloat(rule.minRedemption);
    minRedemptionText = ` (min ${minValue} coins required)`;
  }

  // Format conversion rate if applicable
  let conversionText = "";
  if (rule.conversionRate && parseFloat(rule.conversionRate) > 0) {
    const rate = parseFloat(rule.conversionRate);
    conversionText = ` (${rate} coins = ₹1)`;
  }

  // Construct the final name
  let ruleName = "";
  
  if (rule.applyTarget === "order") {
    ruleName = `Redeem ${redeemAmount}  on ${appliedOn}${limitText}${minRedemptionText}${conversionText}`;
  } else if (rule.applyTarget === "item") {
    ruleName = `Redeem ${redeemAmount}  on ${appliedOn}${limitText}${minRedemptionText}${conversionText}`;
  } else {
    // Fallback for undefined applyTarget
    ruleName = `Redeem ${redeemAmount} ${limitText}${minRedemptionText}${conversionText}`;
  }

  return ruleName;
};



const updateRedemptionRuleName = (ruleId, name) => {
  const updatedRules = redemptionRules.map(rule => 
    rule.id === ruleId ? { ...rule, name, isCustomName: true } : rule
  );
  updateFormData('redemptionRules', updatedRules);
};

 /*  const updateRedemptionRule = (ruleId, key, value) => {
    setRedemptionRules(prev => 
      prev.map(rule => {
        if (rule.id === ruleId) {
          const updatedRule = { ...rule, [key]: value };
          if (!updatedRule.name || updatedRule.name === "") {
            updatedRule.name = generateRedemptionRuleName(updatedRule);
          }
          return updatedRule;
        }
        return rule;
      })
    );
  }; */

const updateRedemptionRule = (ruleId, key, value) => {
  const updatedRules = redemptionRules.map(rule => {
    if (rule.id === ruleId) {
      const updatedRule = { ...rule, [key]: value };
      // Always regenerate name when any field changes (except when explicitly setting the name)
      if (key !== 'name') {
        updatedRule.name = generateRedemptionRuleName(updatedRule);
      }
      return updatedRule;
    }
    return rule;
  });
  updateFormData('redemptionRules', updatedRules);
};

  // Function to update redemption-level conditional operator
  const updateRedemptionRulesConditionalOperator = (ruleId, operator) => {
    const updatedRules = redemptionRules.map(rule => 
      rule.id === ruleId 
        ? { ...rule, nextOperator: operator }
        : rule
    );
    updateFormData('redemptionRules', updatedRules);
  };

 /*  const addRedemptionRule = () => {
    setRedemptionRules(prev => [...prev, { 
      id: uuidv4(), 
      name: "", 
      redeemType: "percentage",
      redeemValue: 0,
      applyTarget: "order"
    }]);
  }; */

  /* const removeRedemptionRule = (ruleId) => {
    setRedemptionRules(prev => prev.filter(rule => rule.id !== ruleId));
  }; */

const addRedemptionRule = () => {
    const updatedRules = [...redemptionRules, { 
      id: uuidv4(), 
      name: "", 
      redeemType: "percentage",
      redeemValue: 0,
      applyTarget: "order",
      limit: 0,
      limitType: "fixed",
      nextOperator: redemptionRules.length > 0 ? "AND" : null // Add conditional operator for non-first criteria
    }];
    updateFormData('redemptionRules', updatedRules);
  };

  const removeRedemptionRule = (ruleId) => {
    const updatedRules = redemptionRules.filter(rule => rule.id !== ruleId);
    updateFormData('redemptionRules', updatedRules);
  };

     const updateRewardRuleApplicableItems = (ruleId, selectedValues) => {
  // FIXED: Preserve user-edited values when deselecting items (redemption)
  const currentRule = redemptionRules.find(r => r.id === ruleId);
  const existingItems = currentRule?.applicableItems || [];
  
  const selectedItems = selectedValues.map(itemId => {
    const item = availableItems.find(item => item.id === itemId);
    if (!item) return null;
    
    // Preserve existing entity if it already has user-edited data
    const existingEntity = existingItems.find(e => e.id === itemId);
    return existingEntity || { id: item.id, name: item.name };
  }).filter(Boolean);

  const updatedRules = redemptionRules.map(rule => {
    if (rule.id === ruleId) {
      const updatedRule = { ...rule, applicableItems: selectedItems };
      // Regenerate name when applicable items change
      updatedRule.name = generateRedemptionRuleName(updatedRule);
      return updatedRule;
    }
    return rule;
  });
  updateFormData('redemptionRules', updatedRules);
};

  return (
    <Card className="w-full">
      <CardContent className="p-8">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h3 className="text-md font-bold text-gray-900 mb-2">How many coins can be redeemed?</h3>
{/*             <p className="text-gray-600">Configure the redemption amounts and targets</p>
 */}          </div>
          <Button onClick={addRedemptionRule} className="shadow-lg">
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Add Redemption Rule
          </Button>
        </div>
        
        <div className="space-y-6">
          {redemptionRules.map((rule,ruleIndex) => (
            <div key={rule.id} >
              {ruleIndex > 0 && (
              <div className="flex justify-center mb-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-1">
{/*                   <label className="block text-xs font-medium text-gray-600 mb-2 text-center">
                    Criteria Relationship
                  </label> */}
                  <Select 
                    value={redemptionRules[ruleIndex-1]?.nextOperator || "AND"} 
                    onValueChange={(value) => updateRedemptionRulesConditionalOperator(redemptionRules[ruleIndex - 1].id, value)}
                  >
                    {conditionalOperators.map((op) => (
                      <SelectItem key={op.value} value={op.value}>
                        <div className="flex flex-col">
                          <span className="font-medium">{op.label}</span>
{/*                           <span className="text-xs text-gray-500">{op.description}</span>
 */}                        </div>
                      </SelectItem>
                    ))}
                  </Select>
                </div>
              </div>
            )}
            <div className="p-6 border border-gray-200 rounded-lg bg-white shadow-sm hover:shadow-md transition-all duration-200">
              <div className="flex justify-between items-start mb-6">
                <div className="flex-1 mr-6">
                 {/*  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Rule Name
                  </label> */}
                  <Input
                    value={rule.name || generateRedemptionRuleName(rule)}
                    onChange={(e) => updateRedemptionRuleName(rule.id, e.target.value)}
                    placeholder="Enter rule name or leave blank for auto-generation"
                    className="text-base"
                  />
                </div>
                {redemptionRules.length > 1 && (
                  <Button 
                    variant="destructive"
                    size="sm"
                    onClick={() => removeRedemptionRule(rule.id)}
                    className="mt-8"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                )}
              </div>
              
              <div>
{/*                 <label className="block text-sm font-semibold text-gray-700 mb-4">
                  Redemption Configuration
                </label> */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                      Redeem Type
                    </label>
                     <Select 
                      value={rule.redeemType} 
                      onValueChange={(value) => updateRedemptionRule(rule.id, "redeemType", value)}
                      placeholder="Select Redeem Type"
                    >
                      <SelectItem value="percentage">Percentage</SelectItem>
                      <SelectItem value="fixed">Fixed Amount</SelectItem>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                      Redeem Value
                    </label>
                    <Input
                      type="number"
                      placeholder="Enter redeem value"
                      value={rule.redeemValue}
                      onChange={(e) => updateRedemptionRule(rule.id, "redeemValue", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                      Redeemed On
                    </label>
                    <Select 
                      value={rule.applyTarget} 
                      onValueChange={(value) => updateRedemptionRule(rule.id, "applyTarget", value)}
                      placeholder="Select Apply Target"
                    >
                      <SelectItem value="order">Order Value</SelectItem>
                      <SelectItem value="coins">Buildcoins</SelectItem>
                      <SelectItem value="item">Item Value</SelectItem>
                    </Select>
                  </div>
                    <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                      Redeemed Items
                    </label>
                    <MultiSelectDropdown
                      options={availableItems}
                      selectedValues={rule.applicableItems ? 
                        rule.applicableItems.map(item => 
                          typeof item === 'object' ? item.id : item
                        ).filter(id => id !== "all") : []
                      }
                      onChange={(selectedValues) => updateRewardRuleApplicableItems(rule.id, selectedValues)}
                      placeholder={availableItems.length > 1 ? "Select items" : "Select category & brand in previous step"}
                    />
                    {availableItems.length <= 1 && (
                      <p className="text-xs text-gray-500 mt-1">
                        Items will appear after selecting category and brand in the previous step
                      </p>
                    )}
                  </div>
                  <div>
                      <label className="block text-xs font-medium text-gray-600 mb-2">
                        Limit
                      </label>
                      <Input
                        type="number"
                        placeholder="Max limit"
                        value={rule.limit}
                        onChange={(e) => updateRedemptionRule(rule.id, "limit", e.target.value)}
                      />
                    </div>
                    <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">Limit Type</label>
                    <Select 
                        value={rule?.limitType}
                      onChange={(value) => updateRedemptionRule(rule.id, 'limitType', value)}
                        placeholder="Select Limit Type"
                      >
                        <SelectItem value="">Select Limit Type</SelectItem>
                        <SelectItem value="fixed">Fixed</SelectItem>
                        <SelectItem value="percentage">Percentage</SelectItem>
                      </Select>
                  </div>
                </div>
              </div>
            </div>
            </div>
          ))}
        </div>

        <div className="flex justify-between mt-8 mb-6">
          <Button variant="outline" onClick={onCancel} size="sm" className="px-4">
            Cancel
          </Button>
          <div className='space-x-2'>
          <Button variant="outline" onClick={onBack} size="sm" className="px-4">
            Back
          </Button>
          <Button onClick={onNext} size="sm" className="px-4 shadow-lg">
            Next
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Expiry Rules Substep Component
const ExpiryRulesSubstep = ({ onNext,onCancel, onBack, formData, updateFormData, mode }) => {
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
const [filteredBrands, setFilteredBrands] = useState([]);

  const [locations, setLocations] = useState([
    { id: "all", name: "All" },
    { id: "hyderabad", name: "Hyderabad" },
    { id: "vijayawada", name: "Vijayawada" },
    { id: "guntur", name: "Guntur" },
    { id: "visakhapatnam", name: "Visakhapatnam" }
  ]);


const customerTypes = [
  {id:"Individual Buyer",name:"Individual Buyer"},
  {id:"Home Builder",name:"Home Builder"},
  {id:"Pro Builder",name:"Pro Builder"},
  {id :"Contractor",name:"Contractor"}


]

useEffect(() => {
  const selectedCategoryName = getSelectedValue('category');
  const selectedBrandName = getSelectedValue('brand');

  const selectedCategory = categories.find(c => c.name === selectedCategoryName);
  const selectedBrand = brands.find(b => b.name === selectedBrandName);

  if (selectedCategory && selectedBrand) {
    // Optional: If you want to load items for this combo
    // fetchItemsByCategoryAndBrand(selectedCategory.id, selectedBrand.id);
  }

  if (selectedCategory && !selectedBrand) {
    filterBrandsByCategory(selectedCategory.id);
  } else if (!selectedCategory && selectedBrand) {
    filterCategoriesByBrand(selectedBrand.id);
  } else {
    setFilteredCategories(categories);
    setFilteredBrands(brands);
  }
}, [formData.expiryRules, categories, brands]);


const getSelectedValue = (fieldKey) => {
  const allConditions = formData.expiryRules.flatMap(rule => rule.conditions);
  const found = [...allConditions].reverse().find(cond => cond.field === fieldKey && cond.value);
  return found?.value || null;
};



const filterBrandsByCategory = async (categoryId) => {
  const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);
  try {
    const res = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}vendor/get-vendorsBy-productCategoryId?product_category_id=${categoryId}`, {
      headers: { 'x-token': token }
    });
    const data = await res.json();
    const formatted = data.data.map(b => ({ id: b._id, name: b.name }));
    setFilteredBrands(formatted);
  } catch (err) {
    console.error('Error filtering brands by category:', err);
  }
};

const filterCategoriesByBrand = async (brandId) => {
  const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);
  try {
    const res = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}product-category/get-productCategoriesBy-vendor_id?vendor_id=${brandId}`, {
      headers: { 'x-token': token }
    });
    const data = await res.json();
    const formatted = data.data.map(c => ({ id: c._id, name: c.product_category_name }));
    setFilteredCategories(formatted);
  } catch (err) {
    console.error('Error filtering categories by brand:', err);
  }
};


  const fields = [
    { label: "Category", value: "category", type: "select", options: filteredCategories.length > 0 ? filteredCategories : categories },
  { label: "Brand", value: "brand", type: "select", options: filteredBrands.length > 0 ? filteredBrands : brands },
    { label: "Amount", value: "amount" },
    { label: "Is Ecommerce", value: "is_ecommerce", type: "select", options: [
      { id: "true", name: "Yes" },
      { id: "false", name: "No" }
    ]},
    { label: "Campaign", value: "campaign" },
    { label: "Event", value: "event" },
    { label: "Location", value: "location", type: "multiselect", options: locations },
    { label: "Items", value: "items", type: "multiselect", options: items },
    { label: "Buyer Type", value:"customer_type", type:"select", options: customerTypes}
  ];

  const operators = [
  { label: "is", value: "==" },
  { label: "is not", value: "!=" },
  { label: "is greater than", value: ">" },
  { label: "is greater than or equal to", value: ">=" },
  { label: "is less than", value: "<" },
  { label: "is less than or equal to", value: "<=" },
  { label: "contains", value: "contains" },
  { label: "does not contain", value: "not_contains" },
  { label: "starts with", value: "starts_with" },
  { label: "ends with", value: "ends_with" },
  { label: "is empty", value: "is_empty" },
  { label: "is not empty", value: "is_not_empty" },
  { label: "exists", value: "exists" },
  { label: "does not exist", value: "not_exists" },
  { label: "is in", value: "in" },
  { label: "is not in", value: "not_in" },
  { label: "includes any of", value: "includes_any" },
  { label: "includes all of", value: "includes_all" },
  { label: "is before", value: "before" },
  { label: "is after", value: "after" },
  { label: "is between", value: "between" },
  { label: "matches pattern", value: "regex" },
  { label: "is like", value: "like" }
];

  // Add conditional operators for combining conditions
  const conditionalOperators = [
    { label: "AND", value: "AND", description: "All conditions must be true" },
    { label: "OR", value: "OR", description: "At least one condition must be true" },
    { label: "NOT", value: "NOT", description: "Condition must not be true" }
  ];

  /* const [expiryRules, setExpiryRules] = useState([
    { 
      id: uuidv4(), 
      name: "", 
      expiryType: "days",
      expiryValue: 60, 
      conditions: [] 
    }
  ]); */

  // Use formData instead of local state
  const expiryRules = formData.expiryRules;


  // Add useEffect to fetch data
  useEffect(() => {
    fetchCategories();
    fetchBrands();
    fetchLocations();
  }, []);

  // Fetch Categories
  const fetchCategories = async () => {
    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}product-category/get-all-productCategories`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const formattedCategories = (data.data).map(cat => ({
        id: cat._id,
        name: cat.product_category_name
      }));
      setCategories(formattedCategories); 
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Brands
  const fetchBrands = async () => {
    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}vendor/get-all-vendors`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const formattedBrands = (data.data).map(brand => ({
        id: brand._id,
        name: brand.name
      }));
      setBrands(formattedBrands); 
    } catch (error) {
      console.error('Error fetching brands:', error);
      setBrands([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Locations
  const fetchLocations = async () => {
    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}hub/get-all-hubs`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const formattedLocations = [
        { id: "all", name: "All" },
        ...(data.data || []).map(location => ({
          id: location._id,
          name: location.city || location.name || location.hub_name
        }))
      ];
      
      setLocations(formattedLocations);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setLocations([
        { id: "all", name: "All" },
        { id: "hyderabad", name: "Hyderabad" },
        { id: "vijayawada", name: "Vijayawada" },
        { id: "guntur", name: "Guntur" },
        { id: "visakhapatnam", name: "Visakhapatnam" }
      ]);
    } finally {
      setLoading(false);
    }
  };

const generateExpiryRuleName = (rule) => {
  if (!rule.expiryType || (!rule.expiryValue && rule.expiryValue !== 0)) {
    return "New Expiry Rule";
  }
  
  // Convert expiryValue to number if it's a string
  const expiryValue = typeof rule.expiryValue === 'string' ? parseFloat(rule.expiryValue) : rule.expiryValue;
  
  // Validate that we have a valid number
  if (isNaN(expiryValue) || expiryValue <= 0) {
    return "New Expiry Rule";
  }
  
  // Format expiry period
  const expiryPeriod = `${expiryValue} ${rule.expiryType}`;
  const hasConditions = rule.conditions && rule.conditions.length > 0;
  
  if (!hasConditions) {
    // Simple expiry without conditions
    return `Coins expire after ${expiryPeriod}`;
  }
  
  // Filter out incomplete conditions
  const validConditions = rule.conditions.filter(cond => 
    cond.field && 
    cond.operator && 
    (cond.value !== "" && cond.value !== null && cond.value !== undefined && 
     !(Array.isArray(cond.value) && cond.value.length === 0))
  );
  
  if (validConditions.length === 0) {
    return `Coins expire after ${expiryPeriod}`;
  }
  
  // Process conditions
  const conditionStrings = validConditions.map(cond => {
    const fieldLabel = fields.find(f => f.value === cond.field)?.label || cond.field;
    const operatorLabel = operators.find(op => op.value === cond.operator)?.label || cond.operator;
    
    let valueString = "";
    
    if (cond.field === "location" && Array.isArray(cond.value)) {
      const locationNames = cond.value.map(locId => {
        const location = locations.find(loc => loc.id === locId);
        return location ? location.name : locId;
      }).filter(Boolean);
      
      if (locationNames.length === 0) {
        valueString = "selected locations";
      } else if (locationNames.length > 2) {
        valueString = `${locationNames.slice(0, 2).join(", ")} and ${locationNames.length - 2} more`;
      } else {
        valueString = locationNames.join(", ");
      }
    } else if (cond.field === "items" && Array.isArray(cond.value)) {
      const itemNames = cond.value.map(itemId => {
        const item = items.find(i => i.id === itemId);
        return item ? item.name : itemId;
      }).filter(Boolean);
      
      if (itemNames.length === 0) {
        valueString = "selected items";
      } else if (itemNames.length > 2) {
        valueString = `${itemNames.slice(0, 2).join(", ")} and ${itemNames.length - 2} more`;
      } else {
        valueString = itemNames.join(", ");
      }
    } else if (cond.field === "category") {
      // Handle category field - check both name and id
      const category = categories.find(cat => cat.name === cond.value || cat.id === cond.value);
      valueString = category ? category.name : cond.value.toString();
    } else if (cond.field === "brand") {
      // Handle brand field - check both name and id
      const brand = brands.find(b => b.name === cond.value || b.id === cond.value);
      valueString = brand ? brand.name : cond.value.toString();
    } else if (cond.field === "amount") {
      valueString = `₹${cond.value}`;
    } else if (cond.field === "is_ecommerce") {
      // Handle both string and boolean values
      if (cond.value === "true" || cond.value === true || cond.value === "Yes") {
        valueString = "Yes";
      } else if (cond.value === "false" || cond.value === false || cond.value === "No") {
        valueString = "No";
      } else {
        valueString = cond.value.toString();
      }
    } else if (cond.field === "campaign") {
      valueString = cond.value.toString();
    } else if (cond.field === "event") {
      valueString = cond.value.toString();
    } else if (Array.isArray(cond.value)) {
      valueString = cond.value.length > 0 ? cond.value.join(", ") : "selected values";
    } else {
      valueString = cond.value.toString();
    }
    
    return `${fieldLabel} ${operatorLabel} ${valueString}`;
  }).filter(str => str !== "");
  
  if (conditionStrings.length === 0) {
    return `Coins expire after ${expiryPeriod}`;
  }
  
  // Create expiry statement with conditions
  let expiryStatement = "";
  if (conditionStrings.length === 1) {
    expiryStatement = `Coins expire after ${expiryPeriod} when ${conditionStrings[0]}`;
  } else if (conditionStrings.length === 2) {
    expiryStatement = `Coins expire after ${expiryPeriod} when ${conditionStrings[0]} and ${conditionStrings[1]}`;
  } else if (conditionStrings.length === 3) {
    expiryStatement = `Coins expire after ${expiryPeriod} when ${conditionStrings[0]}, ${conditionStrings[1]} and ${conditionStrings[2]}`;
  } else {
    expiryStatement = `Coins expire after ${expiryPeriod} when ${conditionStrings[0]}, ${conditionStrings[1]} and ${conditionStrings.length - 2} more conditions are met`;
  }
  
  return expiryStatement;
};


const updateExpiryRuleName = (ruleId, name) => {
  const updatedRules = expiryRules.map(rule => 
    rule.id === ruleId ? { ...rule, name, isCustomName: true } : rule
  );
  updateFormData('expiryRules', updatedRules);
};

 /*  const updateExpiryRule = (ruleId, key, value) => {
    setExpiryRules(prev => 
      prev.map(rule => {
        if (rule.id === ruleId) {
          const updatedRule = { ...rule, [key]: value };
          if (!updatedRule.name || updatedRule.name === "") {
            updatedRule.name = generateExpiryRuleName(updatedRule);
          }
          return updatedRule;
        }
        return rule;
      })
    );
  }; */

  /* const addExpiryCondition = (ruleId) => {
    setExpiryRules(prev => 
      prev.map(rule => 
        rule.id === ruleId 
          ? {
              ...rule,
              conditions: [...rule.conditions, { 
                id: uuidv4(), 
                field: "", 
                operator: "==", 
                value: "" 
              }]
            }
          : rule
      )
    );
  }; */



 /*  const updateExpiryCondition = (ruleId, condId, key, value) => {
    setExpiryRules(prev => 
      prev.map(rule => {
        if (rule.id === ruleId) {
          const updatedRule = {
            ...rule,
            conditions: rule.conditions.map(cond => 
              cond.id === condId ? { ...cond, [key]: value } : cond
            )
          };
          if (!updatedRule.name || updatedRule.name === "") {
            updatedRule.name = generateExpiryRuleName(updatedRule);
          }
          return updatedRule;
        }
        return rule;
      })
    );
  };
 */
 /*  const removeExpiryCondition = (ruleId, condId) => {
    setExpiryRules(prev => 
      prev.map(rule => 
        rule.id === ruleId 
          ? {
              ...rule,
              conditions: rule.conditions.filter(cond => cond.id !== condId)
            }
          : rule
      )
    );
  };
 */
  /* const addExpiryRule = () => {
    setExpiryRules(prev => [...prev, { 
      id: uuidv4(), 
      name: "", 
      expiryType: "days",
      expiryValue: 60, 
      conditions: [] 
    }]);
  }; */

 /*  const removeExpiryRule = (ruleId) => {
    setExpiryRules(prev => prev.filter(rule => rule.id !== ruleId));
  }; */

const updateExpiryRule = (ruleId, key, value) => {
  const updatedRules = expiryRules.map(rule => {
    if (rule.id === ruleId) {
      const updatedRule = { ...rule, [key]: value };
      // Always regenerate name when any field changes (except when explicitly setting the name)
      if (key !== 'name') {
        updatedRule.name = generateExpiryRuleName(updatedRule);
      }
      return updatedRule;
    }
    return rule;
  });
  updateFormData('expiryRules', updatedRules);
};



  const addExpiryCondition = (ruleId) => {
    const updatedRules = expiryRules.map(rule => 
      rule.id === ruleId 
        ? {
            ...rule,
            conditions: [...rule.conditions, { 
              id: uuidv4(), 
              field: "", 
              operator: "==", 
              value: "" ,
              conditionalOperator: rule.conditions.length > 0 ? "AND" : null // Add conditional operator for non-first conditions
            }]
          }
        : rule
    );
    updateFormData('expiryRules', updatedRules);
  };

const updateExpiryCondition = (ruleId, condId, key, value) => {
  const updatedRules = expiryRules.map(rule => {
    if (rule.id === ruleId) {
      const updatedRule = {
        ...rule,
        conditions: rule.conditions.map(cond => 
          cond.id === condId ? { ...cond, [key]: value } : cond
        )
      };
      // Always regenerate name when conditions change
      updatedRule.name = generateExpiryRuleName(updatedRule);
      return updatedRule;
    }
    return rule;
  });
  updateFormData('expiryRules', updatedRules);
};

  // Function to update expiry-level conditional operator
  const updateExpiryRulesConditionalOperator = (ruleId, operator) => {
    const updatedRules = expiryRules.map(rule => 
      rule.id === ruleId 
        ? { ...rule, nextOperator: operator }
        : rule
    );
    updateFormData('expiryRules', updatedRules);
  };

  const removeExpiryCondition = (ruleId, condId) => {
    const updatedRules = expiryRules.map(rule => 
      rule.id === ruleId 
        ? {
            ...rule,
            conditions: rule.conditions.filter(cond => cond.id !== condId)
          }
        : rule
    );
    updateFormData('expiryRules', updatedRules);
  };

  const addExpiryRule = () => {
    const updatedRules = [...expiryRules, { 
      id: uuidv4(), 
      name: "", 
      expiryType: "days",
      expiryValue: 60, 
      conditions: [] ,
      nextOperator: expiryRules.length -1 ? "AND" : null // Add conditional operator for non-first criteria
    }];
    updateFormData('expiryRules', updatedRules);
  };

  const removeExpiryRule = (ruleId) => {
    const updatedRules = expiryRules.filter(rule => rule.id !== ruleId);
    updateFormData('expiryRules', updatedRules);
  };

// Enhanced renderConditions function with conditional operators
  const renderConditions = (conditions, onUpdate, onRemove, parentId, onAdd) => (
    <div className="space-y-3">
      {conditions.map((cond, index) => {
        const selectedField = fields.find(f => f.value === cond.field);
        
        return (
          <div key={cond.id} className="space-y-3">
            {/* Conditional Operator Dropdown - Show for all conditions except the first */}
{index > 0 && (
  <div className="flex justify-center">
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-1">
      <Select 
        value={conditions[index - 1]?.conditionalOperator || "AND"} 
        onValueChange={(value) => onUpdate(parentId, conditions[index - 1].id, "conditionalOperator", value)}
      >
        {conditionalOperators.map((op) => (
          <SelectItem key={op.value} value={op.value}>
            <div className="flex flex-col">
              <span className="font-medium">{op.label}</span>
            </div>
          </SelectItem>
        ))}
      </Select>
    </div>
  </div>
)}


            
            {/* Condition Row */}
            <div className="flex gap-3 items-start p-2 bg-gradient-to-r from-gray-50 to-gray-50/50 rounded-xl border border-gray-100 hover:border-gray-200 transition-all duration-200">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Field</label>
                  <Select 
                    value={cond.field} 
                    onValueChange={(value) => onUpdate(parentId, cond.id, "field", value)}
                    searchable={true}
                    placeholder="Select Field"
                  >
                    {fields.map((f) => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </Select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Operator</label>
                  <Select 
                    value={cond.operator} 
                    onValueChange={(value) => onUpdate(parentId, cond.id, "operator", value)}
                    searchable={true}
                    placeholder="Select Operator"
                  >
                    {operators.map((op) => (
                      <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                    ))}
                  </Select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Value</label>
                  {selectedField?.type === "multiselect" ? (
                    <MultiSelectDropdown
                      options={selectedField.options}
                      selectedValues={Array.isArray(cond.value) ? cond.value : (cond.value ? [cond.value] : [])}
                      onChange={(selectedValues) => onUpdate(parentId, cond.id, "value", selectedValues)}
                      placeholder={`Select ${selectedField.label}`}
                    />
                  ) : selectedField?.type === "select" ? (
                    <Select 
                      value={cond.value} 
                      onValueChange={(value) => onUpdate(parentId, cond.id, "value", value)}
                      searchable={true}
                      placeholder="Select Value"
                    >
                      {selectedField.options.map((option) => (
                        <SelectItem key={option.id} value={option.name}>
                          {option.name}
                        </SelectItem>
                      ))}
                    </Select>
                  ) : (
                    <Input
                      type={selectedField?.value === "amount" ? "number" : "text"}
                      value={cond.value}
                      onChange={(e) => onUpdate(parentId, cond.id, "value", e.target.value)}
                      placeholder="Enter value"
                    />
                  )}
                </div>
              </div>
              
              <div className="pt-6">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => onRemove(parentId, cond.id)}
                  className="w-8 h-8 p-0 rounded-lg"
                >
                  <FontAwesomeIcon icon={faTrash} className="text-xs" />
                </Button>
              </div>
            </div>
          </div>
        );
      })}    
      <Button 
        variant="outline"
        onClick={() => onAdd(parentId)} 
        disabled={loading}
        className="w-full py-1 border-dashed border-2 hover:border-[#f7525a] hover:text-[#f7525a] transition-all duration-200"
      >
        <FontAwesomeIcon icon={faPlus} className="mr-2" />
        {loading ? "Loading..." : "Add Condition"}
      </Button>
    </div>
  );

  return (
    <Card className="w-full">
      <CardContent className="p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h3 className="text-md font-bold text-gray-900 mb-1">Expiry for the coins</h3>
{/*             <p className="text-gray-600">Configure when and how coins expire</p>
 */}          </div>
          <Button onClick={addExpiryRule} className="shadow-lg">
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Add Expiry Rule
          </Button>
        </div>
        
        <div className="space-y-6">
          {expiryRules.map((rule,ruleIndex) => (
            <div key={rule.id}>
                          {ruleIndex > 0 && (
              <div className="flex justify-center mb-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-1">
{/*                   <label className="block text-xs font-medium text-gray-600 mb-2 text-center">
                    Criteria Relationship
                  </label> */}
                  <Select 
                    value={expiryRules[ruleIndex-1]?.nextOperator || "AND"} 
                    onValueChange={(value) => updateExpiryRulesConditionalOperator(expiryRules[ruleIndex - 1].id, value)}
                  >
                    {conditionalOperators.map((op) => (
                      <SelectItem key={op.value} value={op.value}>
                        <div className="flex flex-col">
                          <span className="font-medium">{op.label}</span>
{/*                           <span className="text-xs text-gray-500">{op.description}</span>
 */}                        </div>
                      </SelectItem>
                    ))}
                  </Select>
                </div>
              </div>
            )}



              <div className="p-6 border border-gray-200 rounded-lg bg-white shadow-sm hover:shadow-md transition-all duration-200">
              <div className="flex justify-between items-start mb-6">
                <div className="flex-1 mr-6">
{/*                   <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Rule Name
                  </label> */}
                  <Input
                    value={rule.name || generateExpiryRuleName(rule)}
                    onChange={(e) => updateExpiryRuleName(rule.id, e.target.value)}
                    placeholder="Enter rule name or leave blank for auto-generation"
                    className="text-base"
                  />
                </div>
                {expiryRules.length > 1 && (
                  <Button 
                    variant="destructive"
                    size="sm"
                    onClick={() => removeExpiryRule(rule.id)}
                    className="mt-8"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                )}
              </div>
              
              <div className="space-y-6">
                <div>
{/*                   <label className="block text-sm font-semibold text-gray-700 mb-4">
                    Expiry Configuration
                  </label> */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-2">
                        Expiry Type
                      </label>
                                            <Select 
                        value={rule.expiryType} 
                        onValueChange={(value) => updateExpiryRule(rule.id, "expiryType", value)}
                        placeholder="Select Expiry Type"
                      >
                        <SelectItem value="days">Days</SelectItem>
                        <SelectItem value="months">Months</SelectItem>
                        <SelectItem value="years">Years</SelectItem>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-2">
                        Expiry Value
                      </label>
                      <Input
                        type="number"
                        placeholder="Enter expiry value"
                        value={rule.expiryValue}
                        onChange={(e) => updateExpiryRule(rule.id, "expiryValue", e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-4">
                    Conditions
                  </label>
                  {renderConditions(
                    rule.conditions,
                    updateExpiryCondition,
                    removeExpiryCondition,
                    rule.id,
                    addExpiryCondition
                  )}
                </div>
              </div>
            </div>
            </div>
          ))}
        </div>

        <div className="flex justify-between mt-4 mb-4">
          <Button variant="outline" onClick={onCancel} size="sm" className="px-4">
            Cancel
          </Button>
          <div className='space-x-2'>
          <Button variant="outline" onClick={onBack} size="sm" className="px-4">
            Back
          </Button>
          <Button onClick={onNext} size="sm" className="px-4 shadow-lg">
            Review & Create
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// UPDATED: Review and Create Substep Component
const ReviewAndCreateSubstep = ({ onCancel,onBack, onSubmit, formData, mode }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit();
    } catch (error) {
      console.error(`Error ${mode === 'edit' ? 'updating' : 'creating'} rule:`, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="w-full">
      <CardContent className="p-8">
        <div className="mb-8">
          <h3 className="text-md font-bold text-gray-900 mb-2">{mode === 'edit' ? 'Review Your Changes' : 'Review Your Reward Rule'}</h3>
          {/* <p className="text-gray-600">{mode === 'edit' 
              ? 'Please review all the modifications before updating the rule'
              : 'Please review all the configurations before creating the rule'
            }</p> */}
        </div>
        
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600">Criteria Rules</p>
                  <p className="text-2xl font-bold text-blue-900">{formData.rewardCriteria.length}</p>
                </div>
                <div className="p-2 bg-blue-200 rounded-lg">
                  <FontAwesomeIcon icon={faRules} className="text-blue-600" />
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600">Reward Rules</p>
                  <p className="text-2xl font-bold text-green-900">{formData.rewardRules.length}</p>
                </div>
                <div className="p-2 bg-green-200 rounded-lg">
                  <FontAwesomeIcon icon={faCoins} className="text-green-600" />
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-600">Applicability</p>
                  <p className="text-2xl font-bold text-purple-900">{formData.applicabilityRules.length}</p>
                </div>
                <div className="p-2 bg-purple-200 rounded-lg">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600" />
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl border border-orange-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-600">Expiry Rules</p>
                  <p className="text-2xl font-bold text-orange-900">{formData.expiryRules.length}</p>
                </div>
                <div className="p-2 bg-orange-200 rounded-lg">
                  <FontAwesomeIcon icon={faClock} className="text-orange-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Detailed Review Sections using formData */}
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FontAwesomeIcon icon={faRules} className="mr-2 text-blue-600" />
                Reward Criteria
              </h4>
              <div className="space-y-3">
                {formData.rewardCriteria.map((criteria) => (
                  <div key={criteria.id} className="p-4 bg-white rounded-lg border border-gray-100">
                    <p className="font-medium text-gray-900">{criteria.name || "Reward Criteria"}</p>
                    {(criteria.fromDate || criteria.toDate) && (
                      <p className="text-sm text-gray-600 mt-1">
                        Valid from: {criteria.fromDate || "Not set"} to {criteria.toDate || "Not set"}
                      </p>
                    )}
                    <p className="text-sm text-gray-500 mt-1">
                      {criteria.conditions.length} condition(s)
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FontAwesomeIcon icon={faCoins} className="mr-2 text-green-600" />
                Reward Rules
              </h4>
              <div className="space-y-3">
                {formData.rewardRules.map((rule) => (
                  <div key={rule.id} className="p-4 bg-white rounded-lg border border-gray-100">
                    <p className="font-medium text-gray-900">{rule.name || "Reward Rule"}</p>
                    <p className="text-sm text-gray-600 mt-1">
                      {rule.rewardType === "fixed" ? "Fixed reward" : "Percentage reward"} • 
                      Applied on {rule.applyTarget} value • 
                      Destination: {rule.destination}
                    </p>
                    {rule.limit > 0 && (
                      <p className="text-sm text-gray-500 mt-1">Max limit: {rule.limit}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FontAwesomeIcon icon={faCheckCircle} className="mr-2 text-purple-600" />
                Applicability Rules
              </h4>
              <div className="space-y-3">
                {formData.applicabilityRules.map((rule) => (
                  <div key={rule.id} className="p-4 bg-white rounded-lg border border-gray-100">
                    <p className="font-medium text-gray-900">{rule.name || "Applicability Rule"}</p>
                    <p className="text-sm text-gray-600 mt-1">
                      {rule.conditions.length} condition(s) for coin usage
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FontAwesomeIcon icon={faCoins} className="mr-2 text-orange-600" />
                Redemption Rules
              </h4>
              <div className="space-y-3">
                {formData.redemptionRules.map((rule) => (
                  <div key={rule.id} className="p-4 bg-white rounded-lg border border-gray-100">
                    <p className="font-medium text-gray-900">{rule.name || "Redemption Rule"}</p>
                    <p className="text-sm text-gray-600 mt-1">
                      {rule.redeemType === "percentage" ? "Percentage" : "Fixed"} redemption • 
                      Applied on {rule.applyTarget}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FontAwesomeIcon icon={faClock} className="mr-2 text-orange-600" />
                Expiry Rules
              </h4>
              <div className="space-y-3">
                {formData.expiryRules.map((rule) => (
                  <div key={rule.id} className="p-4 bg-white rounded-lg border border-gray-100">
                    <p className="font-medium text-gray-900">{rule.name || "Coins expire after 60 days"}</p>
                    <p className="text-sm text-gray-600 mt-1">
                      Expires after {rule.expiryValue} {rule.expiryType} • 
                      {rule.conditions.length > 0 ? `${rule.conditions.length} condition(s)` : "No additional conditions"}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-between mt-4 mb-6">
          <Button variant="outline" onClick={onCancel} size="sm" className="px-4">
            Cancel
          </Button>
          <div className='space-x-2'>
          <Button variant="outline" onClick={onBack} size="sm" className="px-4">
            Back to Edit
          </Button>
          <Button 
            onClick={handleSubmit} 
            size="sm" 
            className="px-4 shadow-lg"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Spinner className="mr-2" />
                {mode === 'edit' ? 'Updating Rule...' : 'Creating Rule...'}
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faCheck} className="mr-2" />
                {mode === 'edit' ? 'Update Reward Rule' : 'Create Reward Rule'}
              </>
            )}
          </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Main Reward Rule Builder Component
const RewardRuleBuilder = ({ mode = 'create', existingRuleData = null, criteriaId = null, ruleId = null }) => {
  //const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showCancelPopup, setShowCancelPopup] = useState(false);
  const [isSaving,setIsSaving] = useState(false)
  const navigate = useNavigate();

useEffect(() => {
  if (mode === 'edit' || mode === 'create') {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000); // 2 seconds

    return () => clearTimeout(timer); // clean up
  }
}, [mode]);

  
  // Helper function to transform backend data to frontend format
  const transformBackendDataToFrontend = (backendData) => {
    if (!backendData) return getDefaultFormData();

    return {
      rewardCriteria: backendData.rewardCriteria?.rewardCriteria?.map(criteria => ({
        id: criteria.id || uuidv4(),
        name: criteria.name || "",
        fromDate: convertBackendDateToDisplay(criteria.fromDate),
        toDate: convertBackendDateToDisplay(criteria.toDate),
        conditions: (criteria.conditions || []).map(condition => ({
          ...condition,
          id: condition.id || uuidv4(), // Ensure each condition has a unique ID
          _timestamp: Date.now()
          })),
          nextOperator: criteria.nextOperator
      })) || [getDefaultRewardCriteria()],
      
      rewardRules: backendData.rewardRules?.map(rule => ({
        id: rule.id || uuidv4(),
        name: rule.name || "",
        rewardType: rule.reward?.type || "fixed",
        rewardValue: rule.reward?.value || 0,
        applyTarget: rule.applyTarget || "order",
        destination: rule.destination || "wallet",
        limit: rule.reward?.limit || 0,
        limitType: rule.reward?.limitType || "fixed",
        applicableItems: rule.applicableItems || [],
        nextOperator: rule.nextOperator
      })) || [getDefaultRewardRule()],
      
      applicabilityRules: backendData.applicabilityRules?.map(rule => ({
        id: rule.id || uuidv4(),
        name: rule.name || "",
        //conditions: rule.cconditions || [],
        conditions: (rule.conditions || []).map(condition => ({
          ...condition,
          id: condition.id || uuidv4(), // Ensure each condition has a unique ID
          _timestamp: Date.now()
          })),
        nextOperator: rule.nextOperator
      })) || [getDefaultApplicabilityRule()],
      
      redemptionRules: backendData.redemptionRules?.map(rule => ({
        id: rule.id || uuidv4(),
        name: rule.name || "",
        redeemType: rule.redeem?.type || "percentage",
        redeemValue: rule.redeem?.value || 0,
        limitType: rule.redeem?.limitType || "fixed",
        limit: rule.redeem?.limit || 0,
        applyTarget: rule.applyTarget || "order",
        applicableItems: rule.applicableItems || [],
        nextOperator: rule.nextOperator
      })) || [getDefaultRedemptionRule()],
      
      expiryRules: backendData.expiryRules?.map(rule => ({
        id: rule.id || uuidv4(),
        name: rule.name || "",
        expiryType: rule.expiry?.type || "days",
        expiryValue: rule.expiry?.value || 60,
        conditions: (rule.conditions || []).map(condition => ({
          ...condition,
          id: condition.id || uuidv4(), // Ensure each condition has a unique ID
          _timestamp: Date.now()
          })),
        nextOperator: rule.nextOperator
      })) || [getDefaultExpiryRule()]
    };
  };

  // Helper function to convert backend date to display format
  const convertBackendDateToDisplay = (backendDate) => {
    if (!backendDate) return '';
    
    try {
      // Handle ISO date strings
      if (backendDate.includes('T')) {
        const dateOnly = backendDate.split('T')[0];
        const [year, month, day] = dateOnly.split('-');
        return `${day}/${month}/${year}`;
      }
      
      // Handle YYYY-MM-DD format
      if (backendDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = backendDate.split('-');
        return `${day}/${month}/${year}`;
      }
      
      return backendDate;
    } catch (error) {
      console.error('Error converting backend date:', error);
      return '';
    }
  };

  // Default form data functions
  const getDefaultFormData = () => ({
    rewardCriteria: [getDefaultRewardCriteria()],
    rewardRules: [getDefaultRewardRule()],
    applicabilityRules: [getDefaultApplicabilityRule()],
    redemptionRules: [getDefaultRedemptionRule()],
    expiryRules: [getDefaultExpiryRule()]
  });

  const getDefaultRewardCriteria = () => ({
    id: uuidv4(),
    name: "",
    fromDate: "",
    toDate: "",
    conditions: [],
    nextOperator:"AND"
  });

  const getDefaultRewardRule = () => ({
    id: uuidv4(),
    name: "",
    rewardType: "fixed",
    rewardValue: 0,
    applyTarget: "order",
    destination: "wallet",
    limit: 0,
    limitType: "fixed",
    applicableItems: [],
    nextOperator:"AND"
  });

  const getDefaultApplicabilityRule = () => ({
    id: uuidv4(),
    name: "",
    conditions: [],
    nextOperator:"AND"
  });

  const getDefaultRedemptionRule = () => ({
    id: uuidv4(),
    name: "",
    redeemType: "percentage",
    redeemValue: 0,
    applyTarget: "order",
    limit: 0,
    limitType: "fixed",
    applicableItems: [],
    nextOperator:"AND"

  });

  const getDefaultExpiryRule = () => ({
    id: uuidv4(),
    name: "",
    expiryType: "days",
    expiryValue: 60,
    conditions: [],
    nextOperator:"AND"
  });

  // Initialize form data based on mode
  const [formData, setFormData] = useState(() => {
    if (mode === 'edit' && existingRuleData) {
      return transformBackendDataToFrontend(existingRuleData);
    }
    return getDefaultFormData();
  });


  // LIFTED STATE - All form data stored here
/*   const [formData, setFormData] = useState({
    rewardCriteria: [
      {
        id: uuidv4(),
        name: "",
        fromDate: "",
        toDate: "",
        conditions: []
      }
    ],
    rewardRules: [
      {
        id: uuidv4(),
        name: "",
        rewardType: "fixed",
        rewardValue: 0,
        applyTarget: "order",
        destination: "wallet",
        limit: 0,
        applicableItems: []
      }
    ],
    applicabilityRules: [
      {
        id: uuidv4(),
        name: "",
        conditions: []
      }
    ],
    redemptionRules: [
      {
        id: uuidv4(),
        name: "",
        redeemType: "percentage",
        redeemValue: 0,
        applyTarget: "order"
      }
    ],
    expiryRules: [
      {
        id: uuidv4(),
        name: "",
        expiryType: "days",
        expiryValue: 60,
        conditions: []
      }
    ]
  }); */

 // Update form data when existingRuleData changes
  useEffect(() => {
    if (mode === 'edit' && existingRuleData) {
      setFormData(transformBackendDataToFrontend(existingRuleData));
    }
  }, [mode, existingRuleData]);


  // Update form data function
  const updateFormData = (section, data) => {
    setFormData(prev => ({
      ...prev,
      [section]: data
    }));
  };

  const steps = [
    {
      id: 'criteria',
      title: 'Criteria',
      description: 'When to give coins',
      component: RewardCriteriaSubstep
    },
    {
      id: 'rewards',
      title: 'Rewards',
      description: 'How many coins',
      component: RewardRulesSubstep
    },
    {
      id: 'applicability',
      title: 'Applicability',
      description: 'Where to apply',
      component: ApplicabilityRulesSubstep
    },
    {
      id: 'redemption',
      title: 'Redemption',
      description: 'How to redeem',
      component: RedemptionRulesSubstep
    },
    {
      id: 'expiry',
      title: 'Expiry',
      description: 'When coins expire',
      component: ExpiryRulesSubstep
    },
    {
      id: 'review',
      title: 'Review',
      description: 'Review & create',
      component: ReviewAndCreateSubstep
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

const handleStayHere = () => {
  setShowCancelPopup(false);
}

const handleCancel = () => {
  setShowCancelPopup(true);
}

const handleLeave = () => {
  navigate(-1);
}


  // Start at review step when editing, else start from first step
  const [currentStep, setCurrentStep] = useState(() => (mode === 'edit' ? steps.length - 1 : 0));

 /* const handleSubmit = async () => {
    try {
      console.log('Submitting reward rule with data:', formData);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Clear form data after successful submission
      setFormData({
        rewardCriteria: [{ id: uuidv4(), name: "", fromDate: "", toDate: "", conditions: [] }],
        rewardRules: [{ id: uuidv4(), name: "", rewardType: "fixed", rewardValue: 0, applyTarget: "order", destination: "wallet", limit: 0, applicableItems: [] }],
        applicabilityRules: [{ id: uuidv4(), name: "", conditions: [] }],
        redemptionRules: [{ id: uuidv4(), name: "", redeemType: "percentage", redeemValue: 0, applyTarget: "order" }],
        expiryRules: [{ id: uuidv4(), name: "", expiryType: "days", expiryValue: 60, conditions: [] }]
      });
      
      navigate('/bb/app/rewardrules/rewardruleslist');
    } catch (error) {
      console.error('Error creating reward rule:', error);
      throw error;
    }
  }; */

  /* const handleSubmit = async (attempt = 1) => {
    try {
      //setIsSaving(true);
      //const rulesData = buildRule();
      
      console.log('Saving rules to backend...', formData);
      
      const response = await fetch(`${process.env.REACT_APP_FORE_BUILDCOINS}rules/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      alert('Rules saved successfully to backend!');
      console.log('Rules saved successfully:', result);
      
      // Reset dirty state
      //setIsDirty(false);
      
      // Navigate to rules list
      navigate('/bb/app/rewardrules/rewardruleslist');
      
      return result;
    } catch (error) {
      console.error('Error saving rules to backend:', error);
      alert(`Error saving rules: ${error.message}`);
      
      // Retry logic
      if (attempt < 3) {
        console.log(`Retrying save attempt ${attempt + 1}...`);
        return handleSubmit(attempt + 1);
      }
      
      throw error;
    } finally {
      //setIsSaving(false);
    }
  }; */


     // Simple date conversion function
  const convertDateFormat = (dateString) => {
    if (!dateString) return null;
    
    // If it's DD/MM/YYYY, convert to YYYY-MM-DD
    if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [day, month, year] = dateString.split('/');
      return `${year}-${month}-${day}`;
    }
    
    // If it's already YYYY-MM-DD, return as is
    return dateString;
  };

  // Handle submit for both create and update
  const handleSubmit = async () => {
    try {
      const transformedData = {
        rewardCriteria: formData.rewardCriteria.map(criteria => ({
          id: criteria.id,
          name: criteria.name,
          fromDate: convertDateFormat(criteria.fromDate),
          toDate: convertDateFormat(criteria.toDate),
          conditions: criteria.conditions,
          nextOperator: criteria.nextOperator
        })),
        rewardRules: formData.rewardRules.map(rule => ({
          id: rule.id,
          name: rule.name,
          reward: {
          type: rule.rewardType,
          value: parseFloat(rule.rewardValue) || 0,
          applyOn: rule.applyTarget,
          destination: rule.destination,
          limit: parseFloat(rule.limit) || 0,
          limitOn:rule.limitOn,
          limitType: rule.limitType,
          applicableEntities: rule.applicableItems,
          nextOperator: rule.nextOperator
          },
        })),
        applicabilityRules: formData.applicabilityRules.map(rule => ({
          id: rule.id,
          name: rule.name,
          conditions: rule.conditions,
          nextOperator: rule.nextOperator
        })),
        redemptionRules: formData.redemptionRules.map(rule => ({
          id: rule.id,
          name: rule.name,
          redeem: {
          type: rule.redeemType,
          value: parseFloat(rule.redeemValue) || 0,
          applyOn: rule.applyTarget,
          limit: parseFloat(rule.limit) || 0,
          limitType: rule.limitType || "fixed",
          limitOn:rule.limitOn,
          applicableEntities: rule.applicableItems,
          nextOperator: rule.nextOperator
           },
        })),
        expiryRules: formData.expiryRules.map(rule => ({
          id: rule.id,
          name: rule.name || `Expire after ${rule.expiryValue} ${rule.expiryType}`,
          expiry: {
            type: rule.expiryType,
            value: parseFloat(rule.expiryValue) || 60
          },
          conditions: rule.conditions,
          nextOperator: rule.nextOperator
        }))
      };

      setIsSaving(true);
      console.log('Transformed data for backend:', transformedData);
      
      // Different endpoints for create vs update
      const url = mode === 'edit' 
        ? `${process.env.REACT_APP_FORE_BRICKBUCKET}rules/update?criteriaId=${criteriaId}&rulesId=${ruleId}`
        : `${process.env.REACT_APP_FORE_BRICKBUCKET}rules/create`;
      
      const method = mode === 'edit' ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(transformedData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setIsSaving(false);
      alert(`Rules ${mode === 'edit' ? 'updated' : 'created'} successfully!`);
      
      setIsLoading(true);
      setTimeout(() => {
        //navigate('/bb/app/rewardrules/rewardruleslist');
         window.location.href = '/bb/app/rewardrules/rewardruleslist';
        setIsLoading(false);
      }, 1000);
      
      
    } catch (error) {
      console.error(`Error ${mode === 'edit' ? 'updating' : 'creating'} rules:`, error);
      alert(`Error ${mode === 'edit' ? 'updating' : 'creating'} rules: ${error.message}`);
    }
  };


  if (isLoading) {
    return (
      <div className="h-full bg-white flex items-center justify-center">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} className="text-4xl text-red-500 animate-spin mb-4" />
          <p className="text-gray-600">Loading reward rules...</p>
        </div>
      </div>
    );
  }

  const CurrentStepComponent = steps[currentStep].component;

  return (
    <RewardRuleProvider>
      <div className="h-full flex flex-col bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Fixed Header */}
        <div className="flex-shrink-0 bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto p-4">
            {/* Title Section */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-lg font-bold text-gray-900">
                  {mode === 'edit' ? 'Edit Reward Rule' : 'Create New Reward Rule'}
                </h1>
              </div>
            </div>

            {/* Progress Steps */}
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="flex items-center justify-between overflow-x-auto">
                {steps.map((step, index) => (
                  <div key={step.id} className="flex items-center cursor-pointer hover:opacity-70" onClick={() => setCurrentStep(index)}>
                    <div className="flex flex-col items-center">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center font-semibold text-sm transition-all duration-200 ${
                        index < currentStep 
                          ? 'bg-green-500 text-white' 
                          : index === currentStep 
                          ? 'bg-[#f7525a] text-white' 
                          : 'bg-gray-200 text-gray-500'
                      }`}>
                        {index < currentStep ? (
                          <FontAwesomeIcon icon={faCheck} />
                        ) : (
                          index + 1
                        )}
                      </div>
                      <div className="mt-2 text-center">
                        <p className={`text-sm font-medium ${
                          index <= currentStep ? 'text-gray-900' : 'text-gray-500'
                        }`}>
                          {step.title}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {step.description}
                        </p>
                      </div>
                    </div>
                    {index < steps.length - 1 && (
                      <div className={`w-16 h-0.5 mx-4 transition-all duration-200 ${
                        index < currentStep ? 'bg-green-500' : 'bg-gray-200'
                      }`} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="max-w-7xl mx-auto p-4">
            <CurrentStepComponent 
              onNext={handleNext}
              onBack={handleBack}
              onCancel={handleCancel}
              onSubmit={handleSubmit}
              formData={formData}
              updateFormData={updateFormData}
              mode={mode}
            />
          </div>
        </div>
      </div>

      {/* Cancel Popup */}
      {showCancelPopup && (
        <div className="fixed inset-0 flex items-top justify-center bg-gray-800 bg-opacity-50 z-50">
          <div className="bg-white rounded-b-md shadow-md w-[550px] h-[250px] flex flex-col" style={{ maxWidth: '550px', maxHeight: '250px' }}>
            <div className='bg-[#F9F9FB] flex justify-between items-center w-full h-14 px-6'>
              <h2 className="text-md font-semibold">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-xl text-yellow-500 mr-4 " />
                Leave this page?
              </h2>
              <button onClick={handleStayHere} className="text-white px-1 py-1 rounded-md ">   
                <FontAwesomeIcon icon={faX} className="text-xs text-[#212529] cursor-pointer " />
              </button>
            </div>
            <hr className="border-t border-gray-200 w-full" style={{ borderTopWidth: '1px' }} />
            <div className='pt-4 pl-4 '>
              <div className="w-full items-center p-5">
                <label htmlFor="Other_details" className="block text-sm font-regular text-[#212529] ">
                  If you leave, your unsaved changes will be discarded.
                </label>
              </div>  
            </div>
            <div className="mt-auto w-full">
              <hr className="border-t border-gray-200 w-full" style={{ borderTopWidth: '1px' }} />
              <div className="text-sm flex justify-start items-end space-x-2 px-6 mt-5 mb-4">
                <button className="px-2 py-1 bg-[#408dfb] border-[#f7525a] text-white rounded-md" onClick={handleStayHere}>
                  Stay Here  {isSaving && (
                    <Spinner/>
                  )}
                </button>
                <button onClick={handleLeave} 
                  className="hover:border-[#DDDDDD] focus:border-[#DDDDDD] focus:ring focus:ring-[#DDDDDD] focus:ring-opacity-50  px-2 py-1 bg-[#f5f5f5] text-gray-700 border border-[#DDDDDD] rounded-md">
                  Leave & Discard Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </RewardRuleProvider>
  );
};


export default RewardRuleBuilder;
